import { frictionSubtypeLabel as fallbackSubtypeLabel } from './constants';
import type { AppFont, AppLanguage, AppTheme, AppVisualStyle, DateRange, FrictionSubtype, InterruptionSource, PingType, RescueKind } from './types';

type Dict = Record<string, string>;

const I18N: Record<AppLanguage, Dict> = {
  en: {
    'app.title': 'Rethread',
    'topbar.park': 'Park Thought',
    'topbar.options': 'Options',
    'tab.log': 'Log',
    'tab.rescue': 'Rescue',
    'tab.history': 'History',
    'tab.insights': 'Insights',
    'tab.options': 'Options',
    'options.theme': 'Theme',
    'options.style_language': 'Style language',
    'options.visual_theme': 'Color theme',
    'options.typography': 'Typography',
    'options.app_font': 'App font',
    'options.language': 'Language',
    'options.appearance': 'Appearance',
    'options.compact': 'Compact interface',
    'options.simplified_mode': 'Simplified support flow',
    'options.tips': 'Show helpful tips',
    'options.rescue_defaults': 'Rescue Defaults',
    'options.sound_default': 'Focus timer sound on by default',
    'options.notifications': 'Notifications',
    'options.notifications_desc':
      'Gentle nudges outside active rescues. Timer-finish alerts still appear when you start a timed rescue.',
    'options.notification_style': 'Reminder style',
    'options.notification_style_off': 'Off',
    'options.notification_style_quiet': 'Quiet',
    'options.notification_style_standard': 'Standard',
    'options.notification_exact_alarm': 'Exact timer alarms',
    'options.notification_exact_alarm_granted': 'Allowed',
    'options.notification_exact_alarm_denied': 'Off in Android settings. Background timer alerts may arrive late.',
    'options.notification_exact_alarm_unknown': 'Unknown on this device',
    'options.notification_exact_alarm_open_settings': 'Open exact alarm settings',
    'options.notification_android_battery_note':
      'If background timers still arrive late, set battery usage for this app to Unrestricted or turn off battery optimization in Android settings.',
    'options.notification_android_battery_warning':
      'Android is likely delaying background timer alerts. Turn on exact alarms and remove battery restrictions for this app if you need timer notifications right on time.',
    'options.reentry_nudges': 'Gentle re-entry reminder after a rescue',
    'options.daily_reflection_prompt': 'Optional evening reflection reminder',
    'options.weekly_pattern_prompt': 'Optional weekly pattern reminder',
    'options.safety': 'Safety',
    'options.confirm_delete': 'Confirm before deleting entries',
    'options.monetization': 'Monetization',
    'options.ads_enabled': 'Show ads in non-focus screens',
    'options.personalized_ads': 'Use personalized ads (where allowed)',
    'options.ad_consent_status': 'Ad personalization consent',
    'options.ad_consent_unknown': 'Not set',
    'options.ad_consent_accepted': 'Accepted',
    'options.ad_consent_rejected': 'Rejected',
    'options.ad_consent_not_required': 'Not required in your region',
    'options.withdraw_ad_consent': 'Withdraw ad personalization consent',
    'options.ad_privacy_options': 'Open ad privacy options',
    'options.ads_cmp_notice': 'For EEA/UK users, personalized ads require consent through a certified consent form.',
    'options.ad_consent_withdrawn': 'Ad personalization consent withdrawn.',
    'options.ad_consent_already_off': 'Ad personalization is already disabled.',
    'options.ad_privacy_options_opened': 'Opened ad privacy options.',
    'options.ad_privacy_options_unavailable': 'Ad privacy options are not available on this device yet.',
    'options.ad_privacy_options_failed': 'Could not open ad privacy options.',
    'options.ad_consent_sync_failed': 'Could not refresh ad consent status.',
    'options.billing_desc': 'RevenueCat entitlements decide your tier on this device.',
    'options.billing_configured': 'RevenueCat billing is configured.',
    'options.billing_not_configured': 'RevenueCat billing is not configured yet.',
    'options.billing_status': 'Billing status',
    'options.billing_tier': 'Current tier',
    'options.billing_tier_free': 'Free',
    'options.billing_tier_ad_free': 'Ad-free',
    'options.billing_tier_pro': 'Pro',
    'options.dev_unlock_pro': 'Temporarily unlock Pro on this device',
    'options.dev_unlock_pro_hint': 'Local preview override for testing premium screens.',
    'options.billing_pro_benefits_title': 'What Pro unlocks',
    'options.billing_pro_benefits_body': 'Advanced insights breakdowns, sequences, signal analysis, and CSV exports.',
    'options.billing_check': 'Check purchases',
    'options.billing_restore': 'Restore purchases',
    'options.billing_disclosure':
      'Subscriptions auto-renew until canceled. Billing is handled by Google Play. You can manage or cancel in Google Play subscriptions.',
    'options.billing_plans': 'Available plans',
    'options.billing_no_plans': 'No purchasable plans available right now.',
    'options.billing_buy': 'Buy',
    'options.billing_current': 'Current',
    'options.billing_manage': 'Manage subscription',
    'options.billing_manage_unavailable': 'No subscription management link available yet.',
    'options.billing_check_done': 'Purchase check complete.',
    'options.billing_check_failed': 'Purchase check failed.',
    'options.billing_restore_done': 'Restore complete.',
    'options.billing_restore_failed': 'Restore failed.',
    'options.billing_buy_done': 'Purchase complete.',
    'options.billing_buy_failed': 'Purchase failed or canceled.',
    'options.billing_manage_opened': 'Opened subscription management.',
    'options.billing_diagnostics_title': 'Billing diagnostics',
    'options.billing_diagnostics_run': 'Run billing diagnostics',
    'options.billing_diagnostics_clear_events': 'Clear event log',
    'options.billing_diagnostics_done': 'Billing diagnostics complete.',
    'options.billing_diagnostics_failed': 'Billing diagnostics failed.',
    'options.billing_diagnostics_not_available': 'Not available',
    'options.billing_diagnostics_last_check': 'Last check',
    'options.billing_diagnostics_checks': 'Readiness checks',
    'options.billing_diagnostics_check_ok': 'OK',
    'options.billing_diagnostics_check_needs_action': 'Needs action',
    'options.billing_diagnostics_details': 'Details',
    'options.billing_diagnostics_app_user_id': 'App user ID',
    'options.billing_diagnostics_offering': 'Current offering',
    'options.billing_diagnostics_packages': 'Packages found',
    'options.billing_diagnostics_entitlements': 'Active entitlements',
    'options.billing_diagnostics_subscriptions': 'Active subscriptions',
    'options.billing_diagnostics_management_url': 'Management link',
    'options.billing_diagnostics_recent_events': 'Recent monetization events',
    'options.billing_diagnostics_no_events': 'No monetization events yet.',
    'options.backup': 'Backup & Sync',
    'options.backup_desc': 'Keep your data local, plus optional encrypted backup files for your cloud drive.',
    'options.local_first_title': 'Local-first on the web',
    'options.local_first_body': 'On the web, Rethread stores your data in this browser. No account or sign-in is required.',
    'options.local_first_risk':
      'If you clear browser data or change devices, local data can be lost unless you export it or create an encrypted backup.',
    'options.backup_passphrase': 'Backup passphrase',
    'options.backup_create': 'Create encrypted backup',
    'options.backup_restore': 'Restore backup file',
    'options.backup_status': 'Backup status',
    'options.backup_done': 'Encrypted backup file downloaded.',
    'options.backup_failed': 'Backup failed.',
    'options.restore_done': 'Backup restored successfully.',
    'options.restore_failed': 'Restore failed.',
    'options.sync_in_progress': 'In progress',
    'options.sync_working_action': '{action}...',
    'options.drive': 'Google Drive',
    'options.drive_desc': 'Sync encrypted backups with your Google Drive app data folder.',
    'options.drive_connect': 'Connect Google Drive',
    'options.drive_disconnect': 'Disconnect Google Drive',
    'options.drive_upload': 'Upload encrypted backup',
    'options.drive_restore': 'Restore latest from Drive',
    'options.drive_configured': 'Google Drive client is configured.',
    'options.drive_not_configured': 'Google Drive not configured for this platform.',
    'options.drive_connected': 'Connection: connected',
    'options.drive_not_connected': 'Connection: not connected',
    'options.drive_connected_status': 'Connected to Google Drive.',
    'options.drive_disconnected_status': 'Disconnected from Google Drive.',
    'options.drive_connect_failed': 'Google Drive connection failed.',
    'options.drive_upload_done': 'Encrypted backup uploaded to Google Drive.',
    'options.drive_upload_failed': 'Google Drive upload failed.',
    'options.drive_restore_done': 'Backup restored from Google Drive.',
    'options.drive_restore_failed': 'Google Drive restore failed.',
    'options.help': 'Help',
    'options.replay_title': 'Replay first-run onboarding',
    'options.replay_desc': 'Shows the intro walkthrough again.',
    'options.replay_btn': 'Replay onboarding',
    'options.privacy_data': 'Privacy & Data',
    'options.privacy_desc': 'Review legal policies, contact support, export your data, or delete everything from this device.',
    'options.privacy_rights_sla': 'Privacy requests are handled via support email, typically within 30 days.',
    'options.children_notice': 'Not intended for children under 13 (or your local digital age threshold).',
    'options.privacy_policy': 'Privacy policy',
    'options.terms': 'Terms of use',
    'options.contact_support': 'Contact support',
    'options.privacy_export_data': 'Export my data (JSON)',
    'options.privacy_delete_data': 'Delete all data on this device',
    'options.privacy_export_done': 'Personal data export downloaded.',
    'options.privacy_delete_confirm': 'Delete all local data on this device? This cannot be undone.',
    'options.privacy_delete_done': 'All local data deleted.',
    'options.privacy_delete_failed': 'Failed to delete local data.',
    'log.tip': 'Tip: start the stuck timer as soon as you notice extra drag.',
    'log.flow_title': 'What feels most true right now?',
    'log.flow_body': 'Tap one card. You can add detail or start a rescue after.',
    'log.flow_primary': '1. Notice this moment',
    'log.flow_secondary': '2. Use support if you need it',
    'log.flow_timer_hint': 'Stuck timer:',
    'log.start_stuck': 'Start stuck timer',
    'log.undo': 'Undo',
    'log.stuck_prefix': 'Stuck:',
    'log.how_it_works': 'A gentle way back:',
    'log.slip_helper': 'Start this when you notice you\'re off task. Your next log will include how many minutes you were stuck.',
    'log.quick_sheet_title': 'Quick support',
    'log.quick_sheet_kicker': 'You noticed a friction moment',
    'log.quick_sheet_body': 'Start with one small move.',
    'log.quick_primary_title': 'Start here',
    'log.quick_primary_body': 'This should make the next few minutes a little easier.',
    'log.quick_optional': 'Make this fit better (optional)',
    'log.quick_sheet_skip': 'Save this for now',
    'log.quick_top_pick': 'Recommended support',
    'log.quick_other_rescues': 'Other options',
    'log.simplified_badge': 'Simplified mode',
    'log.simplified_hint': 'Tap one card, then start the support that feels easiest.',
    'rescue.timer_done': 'Rescue timer complete',
    'rescue.pause': 'Pause',
    'rescue.resume': 'Resume',
    'rescue.complete': 'Complete',
    'rescue.snooze': 'Snooze 2 min',
    'rescue.cancel': 'Cancel',
    'rescue.what_helped': 'What helped, even a little?',
    'rescue.followup_optional': 'Optional. A quick tap is enough.',
    'rescue.outcome_helped': 'Helped',
    'rescue.outcome_a_bit': 'A bit',
    'rescue.outcome_not_really': 'Not really',
    'rescue.repeat_10m': 'Do another 10 min',
    'rescue.repeat_minutes': 'Do another {minutes} min',
    'rescue.log_tiny_win': 'Note what helped',
    'rescue.skip': 'Skip for now',
    'rescue.tiny_win_saved': 'Tiny win saved.',
    'rescue.tip': 'Tip: choose one small rescue or support and start there. You can adjust details later.',
    'rescueguide.reset_2m_summary': 'Stand up, unclench, look away from the screen, and take one slow sip of water.',
    'rescueguide.make_smaller_10m_summary': 'Pick one tiny visible next step, then work only on that for 10 minutes.',
    'rescueguide.focus_25m_summary': 'Settle in with one task and let the timer hold the boundary for you.',
    'rescueguide.breathe_90s_summary': 'Follow a slow box breath to bring your body down a notch before you continue.',
    'rescueguide.brain_dump_2m_summary': 'Write the swirl out of your head here first. Short fragments are fine.',
    'rescueguide.walk_5m_summary': 'Move your body for a few minutes, then come back with less pressure in your system.',
    'rescueguide.stand_water_summary': 'Stand up and drink water. The goal is a quick physical reset, not doing more.',
    'rescueguide.one_sentence_summary': 'Write one rough sentence to restart the thread. It does not need to be good.',
    'rescueguide.make_smaller_prompt': 'What is the tiniest visible next step?',
    'rescueguide.brain_dump_prompt': 'What is crowding your head right now?',
    'rescueguide.one_sentence_prompt': 'Write one rough sentence here',
    'rescueguide.make_smaller_action': 'Start 10 min with this step',
    'rescueguide.brain_dump_action': 'Start 2 min dump',
    'rescueguide.one_sentence_action': 'Save this sentence',
    'rescueguide.breathe_intro': 'Breathe with the box.',
    'rescueguide.breathe_in': 'Breathe in',
    'rescueguide.breathe_hold': 'Hold',
    'rescueguide.breathe_out': 'Breathe out',
    'notifications.reentry_title': 'Ready to step back in?',
    'notifications.reentry_body': 'Try one tiny next step. It does not need to be tidy.',
    'notifications.daily_title': 'Want a quick check-in?',
    'notifications.daily_body': 'Take a 20-second look at today before it fades.',
    'notifications.weekly_title': 'Your week has a pattern',
    'notifications.weekly_body': 'Glance at what kept helping and what kept snagging you.',
    'common.saved': 'Saved',
    'common.close': 'Close',
    'common.cancel': 'Cancel',
    'common.none': 'None',
    'common.na': 'n/a',
    'rescuepicker.favorites': 'Favorites',
    'rescuepicker.recent_context_hint': 'Saved from a recent similar moment',
    'rescuepicker.apply_last_context': 'Use last details',
    'rescuepicker.favorite_on': 'Unpin favorite',
    'rescuepicker.favorite_off': 'Pin favorite',
    'rescuepicker.context_step': 'Optional context',
    'rescuepicker.context_hint': 'Open this only if it would make the support fit better.',
    'rescuepicker.rescue_step': 'Start with support',
    'rescuepicker.rescue_hint': 'Pick the rescue or support that feels easiest to begin.',
    'rescuepicker.support_step': 'Make it easier (optional)',
    'rescuepicker.support_hint': 'Only use this if it would make starting easier.',
    'parking.title': 'Parking lot',
    'parking.desc': 'Park the thought so you can return to your task.',
    'parking.placeholder': 'What thought are you parking?',
    'parking.save': 'Save thought',
    'onboarding.welcome': 'Welcome',
    'onboarding.skip': 'Skip',
    'onboarding.step': 'Step {current} of {total}',
    'onboarding.back': 'Back',
    'onboarding.next': 'Next',
    'onboarding.start': 'Start gently',
    'onboarding.log_title': 'Log tab: notice the drag',
    'onboarding.log_body': 'Use Log to tap the friction you feel right now. You can also start the stuck timer first when you notice things getting harder to do.',
    'onboarding.rescue_title': 'Rescue tab: find support',
    'onboarding.rescue_body': 'Go to Rescue and pick one small rescue or support. Keep it simple and start with the easiest thing you can do.',
    'onboarding.park_title': 'Park Thought: clear mental clutter',
    'onboarding.park_body': 'Tap Park Thought in the top bar to park distracting thoughts quickly without losing them, then return to your task.',
    'onboarding.history_title': 'History tab: look back gently',
    'onboarding.history_body': 'History lets you look back at friction moments, open an entry, and add detail only when it feels useful.',
    'onboarding.insights_title': 'Insights tab: notice patterns',
    'onboarding.insights_body': 'Insights helps you notice what adds drag, what seems to help, and what patterns keep showing up over time.',
    'onboarding.options_title': 'Options tab: make the app fit you',
    'onboarding.options_body': 'Use Options to tune reminders, visuals, sounds, and privacy choices so the app feels easier to use.',
    'onboarding.safety_title': 'Privacy and safety basics',
    'onboarding.safety_body':
      'Open Privacy & Data in Options to read policies, contact support, export/delete local data, and review health disclaimers. This app is for everyday self-management support only.',
    'history.empty': 'Nothing here yet for this filter. When you notice a friction moment, it will show up here.',
    'history.rescue_used': 'Rescue used',
    'insights.empty': 'Nothing to read here yet. After a few friction moments and a little support, patterns will start to appear.',
    'insights.today_snapshot': 'Today Snapshot',
    'insights.pings': 'Pings',
    'insights.recovered': 'Recovered',
    'insights.avg_stuck': 'Avg Stuck',
    'insights.patterns': 'Patterns',
    'insights.calendar_view': 'Calendar view',
    'insights.swipe_hint': 'Swipe left/right to view all hours.',
    'insights.top_types': 'Friction patterns',
    'insights.sequences': 'What tends to follow',
    'insights.median_gap': 'median gap',
    'insights.what_helps': 'What seems to ease friction',
    'insights.felt_helpful': 'Felt helpful',
    'insights.outcomes_logged': 'Outcomes logged',
    'insights.small_sample': 'small sample',
    'insights.started': 'Started',
    'insights.completion': 'Completion',
    'insights.resolved': 'Resolved',
    'insights.median_next_ping': 'Typical time until next friction',
    'insights.associated_hint': 'These tend to go with fewer follow-up friction moments.',
    'insights.interruption_sources': 'What adds drag',
    'insights.no_interruptions': 'No interruption tags yet.',
    'insights.friction_subtypes': 'Kinds of friction',
    'insights.no_subtypes': 'No subtype tags yet.',
    'insights.tiny_wins': 'Tiny Wins',
    'insights.captured': 'Noted',
    'insights.completed_rescues': 'Completed rescues',
    'insights.capture_rate': 'Noted rate',
    'insights.latest': 'Latest',
    'insights.none_yet': 'None yet',
    'insights.prep_checklist': 'Prep Checklist',
    'insights.timed_rescues': 'Timed rescues',
    'insights.phone_silenced': 'Phone silenced',
    'insights.tabs_closed': 'Tabs closed',
    'insights.doc_open': 'Doc open',
    'insights.timer_visible': 'Timer visible',
    'insights.friction_start': 'Friction Start',
    'insights.pings_slipping_timer': 'Pings with slipping timer',
    'insights.median_stuck_before_ping': 'Median stuck before ping',
    'insights.mean_stuck_before_ping': 'Mean stuck before ping',
    'insights.parking_lot': 'Parking Lot',
    'insights.no_parked': 'No parked thoughts yet.',
    'insights.export': 'Export',
    'insights.export_json': 'Export JSON',
    'insights.export_csv': 'Export CSV',
    'insights.sections': 'Sections',
    'insights.expand_all': 'Expand all',
    'insights.collapse_all': 'Collapse all',
    'insights.day_breakdown': 'Day breakdown',
    'insights.no_day_data': 'No data',
    'insights.selected_day': 'Selected day',
    'insights.rescues_started': 'Rescues started',
    'insights.top_rescues': 'Top rescues',
    'insights.hour_pattern': 'Hourly pattern',
    'insights.prev_month': 'Previous month',
    'insights.next_month': 'Next month',
    'insights.event_details': 'Event details',
    'insights.starts_at': 'Starts',
    'insights.ends_at': 'Ends',
    'insights.duration': 'Duration',
    'insights.status': 'Status',
    'insights.pro_locked_title': 'Pro feature',
    'insights.pro_locked_body': 'Upgrade to Pro in Options to unlock this deeper analysis.',
    'insights.pro_upgrade_cta': 'Upgrade in Options',
    'rescuepicker.whats_happening': "What's happening?",
    'rescuepicker.pulled_away': 'What pulled you away?',
    'rescuepicker.choose_rescue': 'Choose a rescue',
    'rescuepicker.start': 'Start',
    'rescuepicker.prep_checklist': 'Prep checklist (for timed rescues)',
    'rescuepicker.reset_tip': 'Two-minute reset: stand up, breathe slow, sip water if possible.',
    'rescuepicker.optional_details': 'Optional details',
    'rescuepicker.smallest_step': "What's the smallest next step?",
    'rescuepicker.start_10m_note': 'Start 10-min with note',
    'rescuepicker.sentence_placeholder': 'Write 1 sentence',
    'rescuepicker.save_sentence': 'Save sentence',
    'rescuepicker.focus_sound': 'Use sound on focus timer end',
    'rescuepicker.edit_text': 'Edit rescue text',
    'rescuepicker.save_script': 'Save script',
    'detail.title': 'Ping detail',
    'detail.type': 'Type',
    'detail.stuck_before_ping': 'Stuck before ping',
    'detail.subtype': 'Subtype',
    'detail.interrupted_by': 'Interrupted by',
    'detail.completed': 'completed',
    'detail.canceled': 'canceled',
    'detail.tiny_win': 'tiny win',
    'detail.prep': 'prep',
    'detail.phone': 'phone',
    'detail.tabs': 'tabs',
    'detail.doc': 'doc',
    'detail.timer': 'timer',
    'detail.add_note': 'Add note',
    'detail.save_note': 'Save note',
    'detail.delete_ping': 'Delete ping',
    'app.storage_unavailable': 'Storage unavailable',
    'app.confirm_delete_ping': 'Delete this ping?',
    'legal.title': 'Privacy and Terms',
    'legal.body': 'Please review and accept the Privacy Policy and Terms of Use before continuing.',
    'legal.disclaimer':
      'Rethread is an everyday self-management support tool. It is not a medical device and does not provide diagnosis or treatment.',
    'legal.accept': 'I agree and continue',
    'legal.exit': 'Exit app',
    'legal.doc_title_privacy': 'Privacy Policy',
    'legal.doc_title_terms': 'Terms of Use',
    'legal.doc_open_external': 'Open full web version',
    'legal.doc_last_updated': 'Last updated: March 3, 2026',
    'legal.privacy_text':
      'This app stores your logs, rescue entries, settings, and parked thoughts primarily on your device.\n\nIf you connect backup, encrypted backup files are stored in your own cloud account (for example Google Drive app data folder).\n\nIf subscriptions are enabled, Google Play and RevenueCat process purchase and entitlement data to unlock paid features.\n\nIf ads are enabled, ad providers may process ad-related device and interaction data according to their own policies. In EEA/UK, personalized ads are shown only after consent and can be withdrawn in Options.\n\nYou can export your data and delete all local data from Options -> Privacy & Data.\n\nRethread is an everyday self-management support tool and not medical advice, diagnosis, or treatment.',
    'legal.terms_text':
      'Rethread is provided as-is for personal self-management support.\n\nYou are responsible for your use of the app, your device security, and backup passphrase safety.\n\nThe app may offer optional paid subscriptions. Billing, renewals, and cancellations are handled by the app store provider.\n\nYou may not use the app in ways that break laws, abuse services, or attempt to reverse-engineer paid features.\n\nFeature availability may change over time. We may update these terms and related policies.\n\nIf you do not agree with these terms, stop using the app.'
    ,
    'disclosure.title': 'Data Use Disclosure',
    'disclosure.body': 'Before first use, please confirm you understand how data is handled in this app.',
    'disclosure.local_data': 'I understand my logs and settings are stored locally on this device.',
    'disclosure.cloud_backup': 'I understand cloud backup is optional and uses encrypted files in my own cloud account.',
    'disclosure.billing': 'I understand subscriptions are processed by Google Play and RevenueCat if I purchase a plan.',
    'disclosure.personalized_ads': 'Allow personalized ads where available (optional).',
    'disclosure.continue': 'Save and continue',
    'disclosure.exit': 'Exit app',
    'disclosure.required_hint': 'Please acknowledge all required items to continue.',
    'adsconsent.title': 'Personalized ads consent',
    'adsconsent.body':
      'To show personalized ads in EEA/UK, we need your consent first. You can keep non-personalized ads and change this later in Options.',
    'adsconsent.allow': 'Allow personalized ads',
    'adsconsent.keep_non_personalized': 'Keep non-personalized ads'
  },
  es: {
    'app.title': 'Rethread',
    'topbar.park': 'Guardá idea',
    'topbar.options': 'Opciones',
    'tab.log': 'Registro',
    'tab.rescue': 'Rescate',
    'tab.history': 'Historial',
    'tab.insights': 'Análisis',
    'tab.options': 'Opciones',
    'options.theme': 'Tema',
    'options.style_language': 'Lenguaje visual',
    'options.visual_theme': 'Tema de color',
    'options.typography': 'Tipografía',
    'options.app_font': 'Fuente',
    'options.language': 'Idioma',
    'options.appearance': 'Apariencia',
    'options.compact': 'Interfaz compacta',
    'options.simplified_mode': 'Modo simple (registro rápido)',
    'options.tips': 'Mostrar consejos útiles',
    'options.rescue_defaults': 'Valores de rescate',
    'options.sound_default': 'Sonido activado por defecto en foco',
    'options.notifications': 'Notificaciones',
    'options.notifications_desc':
      'Recordatorios suaves fuera de los rescates activos. Las alertas de fin del temporizador siguen apareciendo cuando iniciás un rescate con tiempo.',
    'options.notification_style': 'Estilo de recordatorio',
    'options.notification_style_off': 'Apagado',
    'options.notification_style_quiet': 'Suave',
    'options.notification_style_standard': 'Normal',
    'options.notification_exact_alarm': 'Alarmas exactas del temporizador',
    'options.notification_exact_alarm_granted': 'Permitidas',
    'options.notification_exact_alarm_denied': 'Están desactivadas en Android. Las alertas de fondo pueden llegar tarde.',
    'options.notification_exact_alarm_unknown': 'Estado desconocido en este dispositivo',
    'options.notification_exact_alarm_open_settings': 'Abrir ajustes de alarmas exactas',
    'options.notification_android_battery_note':
      'Si los temporizadores en segundo plano siguen llegando tarde, poné el uso de batería de esta app en Sin restricciones o desactivá la optimización de batería en Android.',
    'options.notification_android_battery_warning':
      'Android probablemente está demorando las alertas de temporizador en segundo plano. Activá las alarmas exactas y quitá las restricciones de batería para esta app si necesitás avisos a tiempo.',
    'options.reentry_nudges': 'Recordatorio suave para retomar después de un rescate',
    'options.daily_reflection_prompt': 'Recordatorio opcional de revisión al final del día',
    'options.weekly_pattern_prompt': 'Recordatorio opcional del patrón semanal',
    'options.safety': 'Seguridad',
    'options.confirm_delete': 'Confirmar antes de borrar',
    'options.monetization': 'Monetización',
    'options.ads_enabled': 'Mostrar anuncios en pantallas no críticas',
    'options.personalized_ads': 'Usar anuncios personalizados (donde aplique)',
    'options.ad_consent_status': 'Consentimiento de anuncios personalizados',
    'options.ad_consent_unknown': 'Sin definir',
    'options.ad_consent_accepted': 'Aceptado',
    'options.ad_consent_rejected': 'Rechazado',
    'options.ad_consent_not_required': 'No requerido en tu región',
    'options.withdraw_ad_consent': 'Retirar consentimiento de personalización de anuncios',
    'options.ad_privacy_options': 'Abrir opciones de privacidad de anuncios',
    'options.ads_cmp_notice': 'Para usuarios en EEE/Reino Unido, los anuncios personalizados requieren consentimiento en un formulario certificado.',
    'options.ad_consent_withdrawn': 'Se retiró el consentimiento de personalización de anuncios.',
    'options.ad_consent_already_off': 'La personalización de anuncios ya está desactivada.',
    'options.ad_privacy_options_opened': 'Se abrieron las opciones de privacidad de anuncios.',
    'options.ad_privacy_options_unavailable': 'Las opciones de privacidad de anuncios no están disponibles todavía en este dispositivo.',
    'options.ad_privacy_options_failed': 'No se pudieron abrir las opciones de privacidad de anuncios.',
    'options.ad_consent_sync_failed': 'No se pudo actualizar el estado de consentimiento de anuncios.',
    'options.billing_desc': 'Los permisos de RevenueCat definen tu plan en este dispositivo.',
    'options.billing_configured': 'RevenueCat está configurado.',
    'options.billing_not_configured': 'RevenueCat todavía no está configurado.',
    'options.billing_status': 'Estado de facturación',
    'options.billing_tier': 'Plan actual',
    'options.billing_tier_free': 'Gratis',
    'options.billing_tier_ad_free': 'Sin anuncios',
    'options.billing_tier_pro': 'Pro',
    'options.dev_unlock_pro': 'Desbloquear Pro temporalmente en este dispositivo',
    'options.dev_unlock_pro_hint': 'Atajo local para probar pantallas premium.',
    'options.billing_pro_benefits_title': 'Lo que desbloquea Pro',
    'options.billing_pro_benefits_body': 'Desgloses avanzados en Análisis, secuencias, análisis de señales y exportación CSV.',
    'options.billing_check': 'Verificar compras',
    'options.billing_restore': 'Restaurar compras',
    'options.billing_disclosure':
      'Las suscripciones se renuevan automáticamente hasta cancelarlas. La facturación la gestiona Google Play. Podés administrar o cancelar desde Suscripciones de Google Play.',
    'options.billing_plans': 'Planes disponibles',
    'options.billing_no_plans': 'No hay planes comprables por ahora.',
    'options.billing_buy': 'Comprar',
    'options.billing_current': 'Actual',
    'options.billing_manage': 'Gestionar suscripción',
    'options.billing_manage_unavailable': 'Todavía no hay enlace para gestionar la suscripción.',
    'options.billing_check_done': 'Verificación de compras completada.',
    'options.billing_check_failed': 'Falló la verificación de compras.',
    'options.billing_restore_done': 'Restauración completada.',
    'options.billing_restore_failed': 'Falló la restauración.',
    'options.billing_buy_done': 'Compra completada.',
    'options.billing_buy_failed': 'La compra falló o fue cancelada.',
    'options.billing_manage_opened': 'Se abrió la gestión de suscripción.',
    'options.billing_diagnostics_title': 'Diagnóstico de facturación',
    'options.billing_diagnostics_run': 'Ejecutar diagnóstico',
    'options.billing_diagnostics_clear_events': 'Limpiar eventos',
    'options.billing_diagnostics_done': 'Diagnóstico de facturación completado.',
    'options.billing_diagnostics_failed': 'Falló el diagnóstico de facturación.',
    'options.billing_diagnostics_not_available': 'No disponible',
    'options.billing_diagnostics_last_check': 'Última verificación',
    'options.billing_diagnostics_checks': 'Chequeos de estado',
    'options.billing_diagnostics_check_ok': 'OK',
    'options.billing_diagnostics_check_needs_action': 'Requiere acción',
    'options.billing_diagnostics_details': 'Detalles',
    'options.billing_diagnostics_app_user_id': 'ID de usuario de la app',
    'options.billing_diagnostics_offering': 'Offering actual',
    'options.billing_diagnostics_packages': 'Paquetes encontrados',
    'options.billing_diagnostics_entitlements': 'Entitlements activos',
    'options.billing_diagnostics_subscriptions': 'Suscripciones activas',
    'options.billing_diagnostics_management_url': 'Link de gestión',
    'options.billing_diagnostics_recent_events': 'Eventos recientes de monetización',
    'options.billing_diagnostics_no_events': 'Todavía no hay eventos de monetización.',
    'options.backup': 'Respaldo y sincronización',
    'options.backup_desc': 'Tus datos quedan locales, con respaldo cifrado opcional para guardar en tu nube.',
    'options.local_first_title': 'Primero local en la web',
    'options.local_first_body': 'En la web, Rethread guarda tus datos en este navegador. No hace falta cuenta ni iniciar sesión.',
    'options.local_first_risk':
      'Si borras los datos del navegador o cambias de dispositivo, los datos locales pueden perderse salvo que exportes o crees un respaldo cifrado.',
    'options.backup_passphrase': 'Contraseña del respaldo',
    'options.backup_create': 'Crear respaldo cifrado',
    'options.backup_restore': 'Restaurar archivo de respaldo',
    'options.backup_status': 'Estado del respaldo',
    'options.backup_done': 'Se descargó el respaldo cifrado.',
    'options.backup_failed': 'Falló el respaldo.',
    'options.restore_done': 'Respaldo restaurado correctamente.',
    'options.restore_failed': 'Falló la restauración.',
    'options.sync_in_progress': 'En curso',
    'options.sync_working_action': '{action}...',
    'options.drive': 'Google Drive',
    'options.drive_desc': 'Sincronizá respaldos cifrados con la carpeta de datos de la app en Google Drive.',
    'options.drive_connect': 'Conectar Google Drive',
    'options.drive_disconnect': 'Desconectar Google Drive',
    'options.drive_upload': 'Subir respaldo cifrado',
    'options.drive_restore': 'Restaurar último desde Drive',
    'options.drive_configured': 'Cliente de Google Drive configurado.',
    'options.drive_not_configured': 'Google Drive no está configurado para esta plataforma.',
    'options.drive_connected': 'Conexión: conectada',
    'options.drive_not_connected': 'Conexión: no conectada',
    'options.drive_connected_status': 'Google Drive conectado.',
    'options.drive_disconnected_status': 'Google Drive desconectado.',
    'options.drive_connect_failed': 'Falló la conexión con Google Drive.',
    'options.drive_upload_done': 'Respaldo cifrado subido a Google Drive.',
    'options.drive_upload_failed': 'Falló la subida a Google Drive.',
    'options.drive_restore_done': 'Respaldo restaurado desde Google Drive.',
    'options.drive_restore_failed': 'Falló la restauración desde Google Drive.',
    'options.help': 'Ayuda',
    'options.replay_title': 'Repetir onboarding inicial',
    'options.replay_desc': 'Volvé a ver la guía inicial.',
    'options.replay_btn': 'Repetir onboarding',
    'options.privacy_data': 'Privacidad y datos',
    'options.privacy_desc': 'Revisá políticas legales, contactá soporte, exportá tus datos o borrá todo de este dispositivo.',
    'options.privacy_rights_sla': 'Las solicitudes de privacidad se gestionan por email de soporte, normalmente dentro de 30 días.',
    'options.children_notice': 'No está destinada a menores de 13 años (o la edad digital mínima de tu jurisdicción).',
    'options.privacy_policy': 'Política de privacidad',
    'options.terms': 'Términos de uso',
    'options.contact_support': 'Contactar soporte',
    'options.privacy_export_data': 'Exportar mis datos (JSON)',
    'options.privacy_delete_data': 'Borrar todos los datos de este dispositivo',
    'options.privacy_export_done': 'Se descargó la exportación de datos personales.',
    'options.privacy_delete_confirm': '¿Borrar todos los datos locales de este dispositivo? Esta acción no se puede deshacer.',
    'options.privacy_delete_done': 'Se borraron todos los datos locales.',
    'options.privacy_delete_failed': 'No se pudieron borrar los datos locales.',
    'log.tip': 'Tip: arrancá el temporizador de atasco cuando notes más arrastre de lo normal.',
    'log.flow_title': '¿Qué se siente más cierto ahora mismo?',
    'log.flow_body': 'Tocá una tarjeta. Después podés sumar detalle o arrancar un rescate.',
    'log.flow_primary': '1. Notá este momento',
    'log.flow_secondary': '2. Usá apoyo si lo necesitás',
    'log.flow_timer_hint': 'Temporizador de atasco:',
    'log.start_stuck': 'Arrancar temporizador de atasco',
    'log.undo': 'Deshacer',
    'log.stuck_prefix': 'Atascado:',
    'log.how_it_works': 'Una forma amable de volver:',
    'log.slip_helper': 'Inícialo cuando notes que te fuiste de tarea. Tu próximo registro incluirá cuántos minutos estuviste atascado.',
    'log.quick_sheet_title': 'Apoyo rápido',
    'log.quick_sheet_kicker': 'Notaste un momento de fricción',
    'log.quick_sheet_body': 'Empezá con un paso chico.',
    'log.quick_primary_title': 'Empezá acá',
    'log.quick_primary_body': 'Esto debería hacer un poco más livianos los próximos minutos.',
    'log.quick_optional': 'Hacer que encaje mejor (opcional)',
    'log.quick_sheet_skip': 'Guardar esto por ahora',
    'log.quick_top_pick': 'Apoyo recomendado',
    'log.quick_other_rescues': 'Otras opciones',
    'log.simplified_badge': 'Modo simple',
    'log.simplified_hint': 'Tocá una tarjeta y arrancá el apoyo que se sienta más fácil.',
    'rescue.timer_done': 'Temporizador de rescate finalizado',
    'rescue.pause': 'Pausar',
    'rescue.resume': 'Reanudar',
    'rescue.complete': 'Completar',
    'rescue.snooze': 'Posponer 2 min',
    'rescue.cancel': 'Cancelar',
    'rescue.what_helped': '¿Qué ayudó, aunque sea un poco?',
    'rescue.followup_optional': 'Opcional. Con un toque alcanza.',
    'rescue.outcome_helped': 'Ayudó',
    'rescue.outcome_a_bit': 'Un poco',
    'rescue.outcome_not_really': 'No mucho',
    'rescue.repeat_10m': 'Hacé otros 10 min',
    'rescue.repeat_minutes': 'Hacé otros {minutes} min',
    'rescue.log_tiny_win': 'Anotar qué ayudó',
    'rescue.skip': 'Omitir por ahora',
    'rescue.tiny_win_saved': 'Micro logro guardado.',
    'rescue.tip': 'Tip: elegí un rescate o apoyo chico y empezá por ahí. Después podés ajustar detalles.',
    'rescueguide.reset_2m_summary': 'Parate, aflojá el cuerpo, sacá la vista de la pantalla y tomá un sorbo de agua.',
    'rescueguide.make_smaller_10m_summary': 'Elegí un paso mínimo y visible, y hacé solo eso durante 10 minutos.',
    'rescueguide.focus_25m_summary': 'Quedate con una sola tarea y dejá que el temporizador sostenga el límite.',
    'rescueguide.breathe_90s_summary': 'Seguí una respiración en caja para bajar un cambio antes de seguir.',
    'rescueguide.brain_dump_2m_summary': 'Sacá el ruido de tu cabeza escribiéndolo acá. Pueden ser fragmentos.',
    'rescueguide.walk_5m_summary': 'Mové el cuerpo unos minutos y volvé con menos presión encima.',
    'rescueguide.stand_water_summary': 'Parate y tomá agua. La idea es un reinicio físico rápido, no rendir más.',
    'rescueguide.one_sentence_summary': 'Escribí una sola frase para retomar el hilo. No tiene que quedar bien.',
    'rescueguide.make_smaller_prompt': '¿Cuál es el paso más chico y visible?',
    'rescueguide.brain_dump_prompt': '¿Qué te está llenando la cabeza ahora?',
    'rescueguide.one_sentence_prompt': 'Escribí una frase acá',
    'rescueguide.make_smaller_action': 'Arrancar 10 min con este paso',
    'rescueguide.brain_dump_action': 'Arrancar descarga de 2 min',
    'rescueguide.one_sentence_action': 'Guardar esta frase',
    'rescueguide.breathe_intro': 'Respirá con el cuadro.',
    'rescueguide.breathe_in': 'Inhalá',
    'rescueguide.breathe_hold': 'Sostené',
    'rescueguide.breathe_out': 'Exhalá',
    'notifications.reentry_title': '¿Listo para volver?',
    'notifications.reentry_body': 'Probá con un paso mínimo. No tiene que salir prolijo.',
    'notifications.daily_title': '¿Querés un chequeo rápido?',
    'notifications.daily_body': 'Mirá tu día por 20 segundos antes de que se diluya.',
    'notifications.weekly_title': 'Tu semana tiene un patrón',
    'notifications.weekly_body': 'Mirá rápido qué te ayudó y qué te trabó.',
    'common.saved': 'Guardado',
    'common.close': 'Cerrar',
    'common.cancel': 'Cancelar',
    'common.none': 'Ninguno',
    'common.na': 'n/d',
    'rescuepicker.favorites': 'Favoritos',
    'rescuepicker.recent_context_hint': 'Guardado de un momento parecido reciente',
    'rescuepicker.apply_last_context': 'Usar últimos detalles',
    'rescuepicker.favorite_on': 'Desfijar favorito',
    'rescuepicker.favorite_off': 'Fijar favorito',
    'rescuepicker.context_step': 'Contexto opcional',
    'rescuepicker.context_hint': 'Abrilo solo si eso va a hacer que el apoyo encaje mejor.',
    'rescuepicker.rescue_step': 'Empezá con apoyo',
    'rescuepicker.rescue_hint': 'Elegí el rescate o apoyo que se sienta más fácil de arrancar.',
    'rescuepicker.support_step': 'Hacerlo más fácil (opcional)',
    'rescuepicker.support_hint': 'Usalo solo si te va a hacer más fácil arrancar.',
    'parking.title': 'Guardado de ideas',
    'parking.desc': 'Guardá la idea para volver a la tarea.',
    'parking.placeholder': '¿Qué idea querés guardar?',
    'parking.save': 'Guardar pensamiento',
    'onboarding.welcome': 'Bienvenido',
    'onboarding.skip': 'Saltar',
    'onboarding.step': 'Paso {current} de {total}',
    'onboarding.back': 'Atrás',
    'onboarding.next': 'Siguiente',
    'onboarding.start': 'Empezar de a poco',
    'onboarding.log_title': 'Pestaña Registro: notá el arrastre',
    'onboarding.log_body': 'Usá Registro para tocar la fricción que sentís ahora. También podés iniciar antes el temporizador de atasco cuando notás que todo cuesta más.',
    'onboarding.rescue_title': 'Pestaña Rescate: encontrá apoyo',
    'onboarding.rescue_body': 'Ve a Rescate y elegí un rescate o apoyo pequeño. Mantenelo simple y empezá por lo más fácil que puedas hacer.',
    'onboarding.park_title': 'Guardá idea: despejá la cabeza',
    'onboarding.park_body': 'Tocá Guardá idea para guardar ideas distractoras sin perderlas y volver a la tarea.',
    'onboarding.history_title': 'Pestaña Historial: mirá hacia atrás sin presión',
    'onboarding.history_body': 'Historial te deja volver sobre momentos de fricción, abrir una entrada y sumar detalle solo si te sirve.',
    'onboarding.insights_title': 'Pestaña Análisis: notá patrones',
    'onboarding.insights_body': 'Análisis te ayuda a notar qué suma arrastre, qué suele ayudar y qué patrones vuelven con el tiempo.',
    'onboarding.options_title': 'Pestaña Opciones: hacé que la app te calce',
    'onboarding.options_body': 'Usá Opciones para ajustar recordatorios, visuales, sonidos y privacidad para que la app se sienta más fácil de usar.',
    'onboarding.safety_title': 'Privacidad y seguridad básicas',
    'onboarding.safety_body':
      'Abrí Privacidad y datos en Opciones para leer políticas, contactar soporte, exportar/borrar datos locales y revisar descargos de salud. Esta app es solo de apoyo cotidiano para autogestión.',
    'history.empty': 'Todavía no hay nada para este filtro. Cuando notes un momento de fricción, va a aparecer acá.',
    'history.rescue_used': 'Rescate usado',
    'insights.empty': 'Todavía no hay mucho para leer acá. Después de algunos momentos de fricción y algo de apoyo, los patrones van a empezar a aparecer.',
    'insights.today_snapshot': 'Resumen de hoy',
    'insights.pings': 'Registros',
    'insights.recovered': 'Recuperado',
    'insights.avg_stuck': 'Atasco prom.',
    'insights.patterns': 'Patrones',
    'insights.calendar_view': 'Vista de calendario',
    'insights.swipe_hint': 'Desliza izquierda/derecha para ver todas las horas.',
    'insights.top_types': 'Patrones de fricción',
    'insights.sequences': 'Lo que suele venir después',
    'insights.median_gap': 'brecha mediana',
    'insights.what_helps': 'Qué parece aflojar la fricción',
    'insights.felt_helpful': 'Se sintió útil',
    'insights.outcomes_logged': 'Resultados registrados',
    'insights.small_sample': 'muestra pequeña',
    'insights.started': 'Iniciado',
    'insights.completion': 'Completado',
    'insights.resolved': 'Resuelto',
    'insights.median_next_ping': 'Tiempo típico hasta la próxima fricción',
    'insights.associated_hint': 'Esto suele venir con menos momentos de fricción después.',
    'insights.interruption_sources': 'Qué suma arrastre',
    'insights.no_interruptions': 'Aún no hay etiquetas de interrupción.',
    'insights.friction_subtypes': 'Tipos de fricción',
    'insights.no_subtypes': 'Aún no hay subtipos.',
    'insights.tiny_wins': 'Pequeños logros',
    'insights.captured': 'Anotados',
    'insights.completed_rescues': 'Rescates completados',
    'insights.capture_rate': 'Frecuencia de registro',
    'insights.latest': 'Últimos',
    'insights.none_yet': 'Nada todavía',
    'insights.prep_checklist': 'Checklist de preparación',
    'insights.timed_rescues': 'Rescates temporizados',
    'insights.phone_silenced': 'Teléfono silenciado',
    'insights.tabs_closed': 'Pestañas cerradas',
    'insights.doc_open': 'Doc abierto',
    'insights.timer_visible': 'Temporizador visible',
    'insights.friction_start': 'Inicio de fricción',
    'insights.pings_slipping_timer': 'Pings con temporizador de atasco',
    'insights.median_stuck_before_ping': 'Atasco mediano antes del ping',
    'insights.mean_stuck_before_ping': 'Atasco promedio antes del ping',
    'insights.parking_lot': 'Parking mental',
    'insights.no_parked': 'Aún no hay pensamientos aparcados.',
    'insights.export': 'Exportar',
    'insights.export_json': 'Exportar JSON',
    'insights.export_csv': 'Exportar CSV',
    'insights.sections': 'Secciones',
    'insights.expand_all': 'Expandir todo',
    'insights.collapse_all': 'Colapsar todo',
    'insights.day_breakdown': 'Desglose del día',
    'insights.no_day_data': 'Sin datos',
    'insights.selected_day': 'Día seleccionado',
    'insights.rescues_started': 'Rescates iniciados',
    'insights.top_rescues': 'Rescates principales',
    'insights.hour_pattern': 'Patrón por hora',
    'insights.prev_month': 'Mes anterior',
    'insights.next_month': 'Mes siguiente',
    'insights.event_details': 'Detalle del evento',
    'insights.starts_at': 'Inicio',
    'insights.ends_at': 'Fin',
    'insights.duration': 'Duración',
    'insights.status': 'Estado',
    'insights.pro_locked_title': 'Función Pro',
    'insights.pro_locked_body': 'Mejorá a Pro en Opciones para desbloquear este análisis avanzado.',
    'insights.pro_upgrade_cta': 'Mejorar en Opciones',
    'rescuepicker.whats_happening': '¿Qué está pasando?',
    'rescuepicker.pulled_away': '¿Qué te sacó de la tarea?',
    'rescuepicker.choose_rescue': 'Elegí un rescate',
    'rescuepicker.start': 'Arrancar',
    'rescuepicker.prep_checklist': 'Checklist (rescates con tiempo)',
    'rescuepicker.reset_tip': 'Reset de dos minutos: ponte de pie, respira lento, toma agua si puedes.',
    'rescuepicker.optional_details': 'Detalles opcionales',
    'rescuepicker.smallest_step': '¿Cuál es el siguiente pasito?',
    'rescuepicker.start_10m_note': 'Arrancar 10 min con nota',
    'rescuepicker.sentence_placeholder': 'Escribe 1 frase',
    'rescuepicker.save_sentence': 'Guardar frase',
    'rescuepicker.focus_sound': 'Usar sonido al finalizar foco',
    'rescuepicker.edit_text': 'Editar texto de rescates',
    'rescuepicker.save_script': 'Guardar texto',
    'detail.title': 'Detalle del ping',
    'detail.type': 'Tipo',
    'detail.stuck_before_ping': 'Atasco antes del ping',
    'detail.subtype': 'Subtipo',
    'detail.interrupted_by': 'Interrumpido por',
    'detail.completed': 'completado',
    'detail.canceled': 'cancelado',
    'detail.tiny_win': 'pequeño logro',
    'detail.prep': 'preparación',
    'detail.phone': 'teléfono',
    'detail.tabs': 'pestañas',
    'detail.doc': 'doc',
    'detail.timer': 'timer',
    'detail.add_note': 'Agregar nota',
    'detail.save_note': 'Guardar nota',
    'detail.delete_ping': 'Eliminar ping',
    'app.storage_unavailable': 'Almacenamiento no disponible',
    'app.confirm_delete_ping': '¿Eliminar este ping?',
    'legal.title': 'Privacidad y términos',
    'legal.body': 'Revisá y aceptá la Política de Privacidad y los Términos de Uso antes de continuar.',
    'legal.disclaimer':
      'Rethread es una herramienta de apoyo cotidiano para autogestión. No es un dispositivo médico ni brinda diagnóstico o tratamiento.',
    'legal.accept': 'Acepto y continuar',
    'legal.exit': 'Salir de la app',
    'legal.doc_title_privacy': 'Política de privacidad',
    'legal.doc_title_terms': 'Términos de uso',
    'legal.doc_open_external': 'Abrir versión web completa',
    'legal.doc_last_updated': 'Última actualización: 3 de marzo de 2026',
    'legal.privacy_text':
      'Esta app guarda registros, rescates, configuración e ideas guardadas principalmente en tu dispositivo.\n\nSi conectás respaldo, los archivos cifrados se guardan en tu propia cuenta de nube (por ejemplo, carpeta de datos de app en Google Drive).\n\nSi hay suscripciones habilitadas, Google Play y RevenueCat procesan datos de compra y permisos para desbloquear funciones pagas.\n\nSi los anuncios están habilitados, proveedores de anuncios pueden procesar datos del dispositivo e interacción según sus propias políticas. En EEE/Reino Unido, los anuncios personalizados se muestran solo con consentimiento y podés retirarlo en Opciones.\n\nPodés exportar tus datos y borrar todos los datos locales desde Opciones -> Privacidad y datos.\n\nRethread es una herramienta de apoyo cotidiano para autogestión y no brinda diagnóstico ni tratamiento médico.',
    'legal.terms_text':
      'Rethread se ofrece tal como está para apoyo cotidiano de autogestión personal.\n\nVos sos responsable del uso de la app, la seguridad de tu dispositivo y el cuidado de tu contraseña de respaldo.\n\nLa app puede ofrecer suscripciones pagas opcionales. La facturación, renovación y cancelación las gestiona la tienda de apps.\n\nNo podés usar la app para actividades ilegales, abusar servicios o intentar vulnerar funciones pagas.\n\nLa disponibilidad de funciones puede cambiar con el tiempo. Podemos actualizar estos términos y políticas.\n\nSi no estás de acuerdo con estos términos, dejá de usar la app.'
    ,
    'disclosure.title': 'Divulgación sobre uso de datos',
    'disclosure.body': 'Antes del primer uso, confirmá que entendés cómo se manejan los datos en esta app.',
    'disclosure.local_data': 'Entiendo que mis registros y ajustes se guardan localmente en este dispositivo.',
    'disclosure.cloud_backup': 'Entiendo que el respaldo en nube es opcional y usa archivos cifrados en mi propia cuenta.',
    'disclosure.billing': 'Entiendo que las suscripciones se procesan por Google Play y RevenueCat si compro un plan.',
    'disclosure.personalized_ads': 'Permitir anuncios personalizados cuando estén disponibles (opcional).',
    'disclosure.continue': 'Guardar y continuar',
    'disclosure.exit': 'Salir de la app',
    'disclosure.required_hint': 'Confirmá todos los puntos obligatorios para continuar.',
    'adsconsent.title': 'Consentimiento de anuncios personalizados',
    'adsconsent.body':
      'Para mostrar anuncios personalizados en EEE/Reino Unido, primero necesitamos tu consentimiento. Podés mantener anuncios no personalizados y cambiar esto luego en Opciones.',
    'adsconsent.allow': 'Permitir anuncios personalizados',
    'adsconsent.keep_non_personalized': 'Mantener anuncios no personalizados'
  },
  zh: {
    'app.title': 'Rethread',
    'topbar.park': '暂存想法',
    'topbar.options': '选项',
    'tab.log': '记录',
    'tab.rescue': '救援',
    'tab.history': '历史',
    'tab.insights': '洞察',
    'tab.options': '选项',
    'options.theme': '主题',
    'options.style_language': '样式语言',
    'options.visual_theme': '颜色主题',
    'options.typography': '字体',
    'options.app_font': '应用字体',
    'options.language': '语言',
    'options.appearance': '外观',
    'options.compact': '紧凑界面',
    'options.simplified_mode': '简化快速记录模式',
    'options.tips': '显示提示',
    'options.rescue_defaults': '救援默认设置',
    'options.sound_default': '专注计时默认开启声音',
    'options.notifications': '通知',
    'options.notifications_desc': '仅用于主动救援之外的轻提醒。开始计时救援后，计时结束提醒仍会出现。',
    'options.notification_style': '提醒风格',
    'options.notification_style_off': '关闭',
    'options.notification_style_quiet': '安静',
    'options.notification_style_standard': '标准',
    'options.notification_exact_alarm': '精确定时闹钟',
    'options.notification_exact_alarm_granted': '已允许',
    'options.notification_exact_alarm_denied': 'Android 设置中已关闭。后台计时提醒可能会变晚。',
    'options.notification_exact_alarm_unknown': '此设备上状态未知',
    'options.notification_exact_alarm_open_settings': '打开精确闹钟设置',
    'options.notification_android_battery_note':
      '如果后台计时提醒仍然偏晚，请在 Android 设置里将此应用的电池使用改为“不受限制”或关闭电池优化。',
    'options.notification_android_battery_warning':
      'Android 很可能正在延后后台计时提醒。如果你需要定时提醒准时到达，请为此应用开启精确闹钟并取消电池限制。',
    'options.reentry_nudges': '救援后轻柔提醒重新开始',
    'options.daily_reflection_prompt': '可选的晚间回顾提醒',
    'options.weekly_pattern_prompt': '可选的每周模式提醒',
    'options.safety': '安全',
    'options.confirm_delete': '删除前确认',
    'options.monetization': '变现',
    'options.ads_enabled': '在非专注页面显示广告',
    'options.personalized_ads': '使用个性化广告（适用地区）',
    'options.ad_consent_status': '个性化广告同意状态',
    'options.ad_consent_unknown': '未设置',
    'options.ad_consent_accepted': '已同意',
    'options.ad_consent_rejected': '已拒绝',
    'options.ad_consent_not_required': '你所在地区不要求',
    'options.withdraw_ad_consent': '撤回个性化广告同意',
    'options.ad_privacy_options': '打开广告隐私选项',
    'options.ads_cmp_notice': '对于 EEA/英国用户，个性化广告需要通过认证的同意流程获取授权。',
    'options.ad_consent_withdrawn': '已撤回个性化广告同意。',
    'options.ad_consent_already_off': '个性化广告已关闭。',
    'options.ad_privacy_options_opened': '已打开广告隐私选项。',
    'options.ad_privacy_options_unavailable': '该设备暂不可用广告隐私选项。',
    'options.ad_privacy_options_failed': '无法打开广告隐私选项。',
    'options.ad_consent_sync_failed': '无法刷新广告同意状态。',
    'options.billing_desc': '本设备上的档位由 RevenueCat 权益决定。',
    'options.billing_configured': 'RevenueCat 已配置。',
    'options.billing_not_configured': 'RevenueCat 尚未配置。',
    'options.billing_status': '计费状态',
    'options.billing_tier': '当前档位',
    'options.billing_tier_free': '免费',
    'options.billing_tier_ad_free': '去广告',
    'options.billing_tier_pro': '专业版',
    'options.dev_unlock_pro': '在这台设备上临时解锁 Pro',
    'options.dev_unlock_pro_hint': '用于测试高级界面的本地预览开关。',
    'options.billing_pro_benefits_title': 'Pro 解锁内容',
    'options.billing_pro_benefits_body': '更深入的洞察分解、序列分析、信号分析与 CSV 导出。',
    'options.billing_check': '检查购买',
    'options.billing_restore': '恢复购买',
    'options.billing_disclosure': '订阅会自动续费，直到你取消。计费由 Google Play 处理。你可在 Google Play 订阅中管理或取消。',
    'options.billing_plans': '可购买方案',
    'options.billing_no_plans': '当前没有可购买方案。',
    'options.billing_buy': '购买',
    'options.billing_current': '当前方案',
    'options.billing_manage': '管理订阅',
    'options.billing_manage_unavailable': '暂无可用的订阅管理链接。',
    'options.billing_check_done': '购买检查完成。',
    'options.billing_check_failed': '购买检查失败。',
    'options.billing_restore_done': '恢复完成。',
    'options.billing_restore_failed': '恢复失败。',
    'options.billing_buy_done': '购买完成。',
    'options.billing_buy_failed': '购买失败或已取消。',
    'options.billing_manage_opened': '已打开订阅管理。',
    'options.billing_diagnostics_title': '计费诊断',
    'options.billing_diagnostics_run': '运行计费诊断',
    'options.billing_diagnostics_clear_events': '清除事件日志',
    'options.billing_diagnostics_done': '计费诊断完成。',
    'options.billing_diagnostics_failed': '计费诊断失败。',
    'options.billing_diagnostics_not_available': '不可用',
    'options.billing_diagnostics_last_check': '最近检查',
    'options.billing_diagnostics_checks': '就绪检查',
    'options.billing_diagnostics_check_ok': '正常',
    'options.billing_diagnostics_check_needs_action': '需处理',
    'options.billing_diagnostics_details': '详情',
    'options.billing_diagnostics_app_user_id': '应用用户 ID',
    'options.billing_diagnostics_offering': '当前 offering',
    'options.billing_diagnostics_packages': '找到的套餐数',
    'options.billing_diagnostics_entitlements': '激活权益',
    'options.billing_diagnostics_subscriptions': '激活订阅',
    'options.billing_diagnostics_management_url': '管理链接',
    'options.billing_diagnostics_recent_events': '最近的变现事件',
    'options.billing_diagnostics_no_events': '还没有变现事件。',
    'options.backup': '备份与同步',
    'options.backup_desc': '数据保持本地，同时可选创建加密备份文件保存到你的云盘。',
    'options.local_first_title': '网页端以本地为主',
    'options.local_first_body': '在网页端，Rethread 会把数据保存在当前浏览器中。不需要账号，也不需要登录。',
    'options.local_first_risk': '如果你清除浏览器数据或更换设备，本地数据可能会丢失，除非你先导出或创建加密备份。',
    'options.backup_passphrase': '备份口令',
    'options.backup_create': '创建加密备份',
    'options.backup_restore': '恢复备份文件',
    'options.backup_status': '备份状态',
    'options.backup_done': '加密备份文件已下载。',
    'options.backup_failed': '备份失败。',
    'options.restore_done': '备份恢复成功。',
    'options.restore_failed': '恢复失败。',
    'options.sync_in_progress': '进行中',
    'options.sync_working_action': '{action}...',
    'options.drive': 'Google Drive',
    'options.drive_desc': '将加密备份同步到 Google Drive 的应用数据目录。',
    'options.drive_connect': '连接 Google Drive',
    'options.drive_disconnect': '断开 Google Drive',
    'options.drive_upload': '上传加密备份',
    'options.drive_restore': '从 Drive 恢复最新备份',
    'options.drive_configured': 'Google Drive 客户端已配置。',
    'options.drive_not_configured': '此平台尚未配置 Google Drive。',
    'options.drive_connected': '连接状态：已连接',
    'options.drive_not_connected': '连接状态：未连接',
    'options.drive_connected_status': '已连接 Google Drive。',
    'options.drive_disconnected_status': '已断开 Google Drive。',
    'options.drive_connect_failed': '连接 Google Drive 失败。',
    'options.drive_upload_done': '加密备份已上传到 Google Drive。',
    'options.drive_upload_failed': '上传到 Google Drive 失败。',
    'options.drive_restore_done': '已从 Google Drive 恢复备份。',
    'options.drive_restore_failed': '从 Google Drive 恢复失败。',
    'options.help': '帮助',
    'options.replay_title': '重新播放新手引导',
    'options.replay_desc': '再次显示引导流程。',
    'options.replay_btn': '重播引导',
    'options.privacy_data': '隐私与数据',
    'options.privacy_desc': '查看政策、联系支持、导出你的数据，或删除本设备上的全部数据。',
    'options.privacy_rights_sla': '隐私请求通过支持邮箱处理，通常会在 30 天内完成。',
    'options.children_notice': '本应用不面向 13 岁以下儿童（或你所在地区的最低数字年龄）。',
    'options.privacy_policy': '隐私政策',
    'options.terms': '使用条款',
    'options.contact_support': '联系支持',
    'options.privacy_export_data': '导出我的数据（JSON）',
    'options.privacy_delete_data': '删除本设备上的全部数据',
    'options.privacy_export_done': '个人数据导出已下载。',
    'options.privacy_delete_confirm': '删除本设备上的所有本地数据？此操作无法撤销。',
    'options.privacy_delete_done': '所有本地数据已删除。',
    'options.privacy_delete_failed': '删除本地数据失败。',
    'log.tip': '提示：一发现自己跑题，就启动卡住计时器。',
    'log.flow_title': '现在最符合你状态的是哪一种？',
    'log.flow_body': '点一个卡片。之后再补一点细节或直接开始救援。',
    'log.flow_primary': '1. 先记下这一刻',
    'log.flow_secondary': '2. 需要时再用辅助',
    'log.flow_timer_hint': '卡住计时：',
    'log.start_stuck': '开始卡住计时',
    'log.undo': '撤销',
    'log.stuck_prefix': '卡住：',
    'log.how_it_works': '温和回来的方式：',
    'log.slip_helper': '当你发现自己偏离任务时启动。下一次记录会包含卡住时长。',
    'log.quick_sheet_title': '快速支持',
    'log.quick_sheet_kicker': '你注意到了一个卡住的时刻',
    'log.quick_sheet_body': '先做一个小动作。',
    'log.quick_primary_title': '先从这里开始',
    'log.quick_primary_body': '这应该能让接下来的几分钟轻一点。',
    'log.quick_optional': '让它更贴合一点（可选）',
    'log.quick_sheet_skip': '先把这一刻记下来',
    'log.quick_top_pick': '推荐支持',
    'log.quick_other_rescues': '其他选项',
    'log.simplified_badge': '简化模式',
    'log.simplified_hint': '点一下卡片，然后从最容易开始的支持开始。',
    'rescue.timer_done': '救援计时结束',
    'rescue.pause': '暂停',
    'rescue.resume': '继续',
    'rescue.complete': '完成',
    'rescue.snooze': '延后 2 分钟',
    'rescue.cancel': '取消',
    'rescue.what_helped': '什么有帮助，哪怕一点点？',
    'rescue.followup_optional': '可选。点一下就够了。',
    'rescue.outcome_helped': '有帮助',
    'rescue.outcome_a_bit': '有一点',
    'rescue.outcome_not_really': '没太大帮助',
    'rescue.repeat_10m': '再做 10 分钟',
    'rescue.repeat_minutes': '再做 {minutes} 分钟',
    'rescue.log_tiny_win': '记下什么有帮助',
    'rescue.skip': '暂时跳过',
    'rescue.tiny_win_saved': '小进展已保存。',
    'rescue.tip': '提示：先选一个小小的救援或支持，从那里开始。细节之后再调。',
    'rescueguide.reset_2m_summary': '站起来，放松身体，眼睛离开屏幕，再慢慢喝一口水。',
    'rescueguide.make_smaller_10m_summary': '只选一个最小、最具体的下一步，然后只做它 10 分钟。',
    'rescueguide.focus_25m_summary': '只守住一件事，让计时器替你看住边界。',
    'rescueguide.breathe_90s_summary': '跟着方块呼吸，让身体先慢下来一点，再继续。',
    'rescueguide.brain_dump_2m_summary': '先把脑子里的乱流写在这里。写碎片也可以。',
    'rescueguide.walk_5m_summary': '先动一动身体几分钟，再带着更少的压力回来。',
    'rescueguide.stand_water_summary': '站起来喝点水。目标是让身体快一点回稳，不是立刻做更多。',
    'rescueguide.one_sentence_summary': '先写一句粗糙的话，把线头重新接上就够了。',
    'rescueguide.make_smaller_prompt': '最小、最看得见的下一步是什么？',
    'rescueguide.brain_dump_prompt': '现在有什么东西挤满了你的脑子？',
    'rescueguide.one_sentence_prompt': '先在这里写一句',
    'rescueguide.make_smaller_action': '带着这一步开始 10 分钟',
    'rescueguide.brain_dump_action': '开始 2 分钟倾倒',
    'rescueguide.one_sentence_action': '保存这句话',
    'rescueguide.breathe_intro': '跟着方块呼吸。',
    'rescueguide.breathe_in': '吸气',
    'rescueguide.breathe_hold': '停住',
    'rescueguide.breathe_out': '呼气',
    'notifications.reentry_title': '准备重新开始了吗？',
    'notifications.reentry_body': '先试一个很小的下一步，不需要做得漂亮。',
    'notifications.daily_title': '想快速回看一下吗？',
    'notifications.daily_body': '用 20 秒看看今天的情况，趁它还没散掉。',
    'notifications.weekly_title': '这一周有规律可看',
    'notifications.weekly_body': '看看什么经常有帮助，什么经常卡住你。',
    'common.saved': '已保存',
    'common.close': '关闭',
    'common.cancel': '取消',
    'common.none': '无',
    'common.na': '无',
    'rescuepicker.favorites': '收藏',
    'rescuepicker.recent_context_hint': '来自最近一次相似情况的已保存内容',
    'rescuepicker.apply_last_context': '使用上次详情',
    'rescuepicker.favorite_on': '取消置顶收藏',
    'rescuepicker.favorite_off': '置顶收藏',
    'rescuepicker.context_step': '可选背景',
    'rescuepicker.context_hint': '只有当它能让支持更贴合时再打开。',
    'rescuepicker.rescue_step': '先从支持开始',
    'rescuepicker.rescue_hint': '选一个最容易开始的救援或支持。',
    'rescuepicker.support_step': '让它更容易一点（可选）',
    'rescuepicker.support_hint': '只有当它能让开始更容易时再用。',
    'parking.title': '想法暂存区',
    'parking.desc': '先把想法暂存，回到当前任务。',
    'parking.placeholder': '你想暂存什么想法？',
    'parking.save': '保存想法',
    'onboarding.welcome': '欢迎',
    'onboarding.skip': '跳过',
    'onboarding.step': '第 {current} / {total} 步',
    'onboarding.back': '上一步',
    'onboarding.next': '下一步',
    'onboarding.start': '开始记录',
    'history.empty': '这个筛选下还没有内容。等你注意到一次摩擦时，它就会出现在这里。',
    'history.rescue_used': '已使用救援',
    'insights.empty': '这里还没什么可读的。经过几个摩擦时刻和一点支持之后，模式会慢慢出现。',
    'insights.today_snapshot': '今日快照',
    'insights.pings': '记录',
    'insights.recovered': '已恢复',
    'insights.avg_stuck': '平均卡住',
    'insights.patterns': '模式',
    'insights.calendar_view': '日历视图',
    'insights.swipe_hint': '左右滑动查看全部小时。',
    'insights.top_types': '摩擦模式',
    'insights.sequences': '接下来常发生什么',
    'insights.median_gap': '中位间隔',
    'insights.what_helps': '什么似乎在减轻摩擦',
    'insights.felt_helpful': '主观有帮助',
    'insights.outcomes_logged': '已记录结果',
    'insights.small_sample': '样本较小',
    'insights.started': '开始次数',
    'insights.completion': '完成率',
    'insights.resolved': '缓解率',
    'insights.median_next_ping': '到下一次摩擦的典型间隔',
    'insights.associated_hint': '这些通常伴随着更少的后续摩擦时刻。',
    'insights.interruption_sources': '什么在增加阻力',
    'insights.no_interruptions': '还没有中断标签。',
    'insights.friction_subtypes': '摩擦的类型',
    'insights.no_subtypes': '还没有子类型标签。',
    'insights.tiny_wins': '小进展',
    'insights.captured': '已记下',
    'insights.completed_rescues': '已完成救援',
    'insights.capture_rate': '记下频率',
    'insights.latest': '最近',
    'insights.none_yet': '暂无',
    'insights.prep_checklist': '准备清单',
    'insights.timed_rescues': '计时救援',
    'insights.phone_silenced': '手机静音',
    'insights.tabs_closed': '关闭干扰标签页',
    'insights.doc_open': '目标文档已打开',
    'insights.timer_visible': '计时器可见',
    'insights.friction_start': '卡住起点',
    'insights.pings_slipping_timer': '带卡住计时的记录',
    'insights.median_stuck_before_ping': '记录前卡住中位时长',
    'insights.mean_stuck_before_ping': '记录前卡住平均时长',
    'insights.parking_lot': '想法暂存',
    'insights.no_parked': '还没有暂存想法。',
    'insights.export': '导出',
    'insights.export_json': '导出 JSON',
    'insights.export_csv': '导出 CSV',
    'insights.sections': '分区',
    'insights.expand_all': '全部展开',
    'insights.collapse_all': '全部折叠',
    'insights.day_breakdown': '当日分解',
    'insights.no_day_data': '无数据',
    'insights.selected_day': '已选日期',
    'insights.rescues_started': '已开始救援',
    'insights.top_rescues': '主要救援',
    'insights.hour_pattern': '小时分布',
    'insights.prev_month': '上个月',
    'insights.next_month': '下个月',
    'insights.event_details': '事件详情',
    'insights.starts_at': '开始',
    'insights.ends_at': '结束',
    'insights.duration': '时长',
    'insights.status': '状态',
    'insights.pro_locked_title': 'Pro 功能',
    'insights.pro_locked_body': '在选项中升级到 Pro 以解锁更深入分析。',
    'insights.pro_upgrade_cta': '去选项升级',
    'rescuepicker.whats_happening': '现在发生了什么？',
    'rescuepicker.pulled_away': '是什么把你拉走了？',
    'rescuepicker.choose_rescue': '选择一个救援动作',
    'rescuepicker.start': '开始',
    'rescuepicker.prep_checklist': '准备清单（计时救援）',
    'rescuepicker.reset_tip': '两分钟重置：起身，慢呼吸，如可行喝点水。',
    'rescuepicker.optional_details': '可选细节',
    'rescuepicker.smallest_step': '最小的下一步是什么？',
    'rescuepicker.start_10m_note': '带备注开始10分钟',
    'rescuepicker.sentence_placeholder': '写一句话',
    'rescuepicker.save_sentence': '保存句子',
    'rescuepicker.focus_sound': '专注计时结束时播放声音',
    'rescuepicker.edit_text': '编辑救援文案',
    'rescuepicker.save_script': '保存文案',
    'detail.title': '记录详情',
    'detail.type': '类型',
    'detail.stuck_before_ping': '记录前卡住',
    'detail.subtype': '子类型',
    'detail.interrupted_by': '被什么打断',
    'detail.completed': '完成',
    'detail.canceled': '取消',
    'detail.tiny_win': '小进展',
    'detail.prep': '准备',
    'detail.phone': '手机',
    'detail.tabs': '标签页',
    'detail.doc': '文档',
    'detail.timer': '计时器',
    'detail.add_note': '添加备注',
    'detail.save_note': '保存备注',
    'detail.delete_ping': '删除记录',
    'app.storage_unavailable': '存储不可用',
    'app.confirm_delete_ping': '删除这条记录吗？',
    'legal.title': '隐私与条款',
    'legal.body': '继续前请先查看并同意《隐私政策》和《使用条款》。',
    'legal.disclaimer': 'Rethread 是效率支持工具，不是医疗器械，也不提供诊断或治疗。',
    'legal.accept': '同意并继续',
    'legal.exit': '退出应用',
    'legal.doc_title_privacy': '隐私政策',
    'legal.doc_title_terms': '使用条款',
    'legal.doc_open_external': '打开完整网页版本',
    'legal.doc_last_updated': '最后更新：2026年3月3日',
    'legal.privacy_text':
      '本应用主要将日志、救援记录、设置和暂存想法存储在你的设备本地。\n\n若你启用备份，加密备份文件会保存在你自己的云账户中（例如 Google Drive 应用数据目录）。\n\n若启用订阅，Google Play 与 RevenueCat 会处理购买和权益数据以解锁付费功能。\n\n若启用广告，广告提供方可能按其政策处理设备与广告交互数据。对于 EEA/英国，个性化广告仅在同意后展示，且可在“选项”中撤回。\n\n你可以在“选项 -> 隐私与数据”中导出数据并删除本地全部数据。\n\nRethread 是效率支持工具，不提供医疗建议、诊断或治疗。',
    'legal.terms_text':
      'Rethread 以“现状”提供，用于效率支持与个人自我管理。\n\n你需自行负责应用使用、设备安全以及备份口令的保管。\n\n应用可能提供可选订阅。计费、续订和取消由应用商店处理。\n\n你不得将本应用用于违法行为、滥用服务或尝试绕过付费功能。\n\n功能可能随时间调整，我们可能更新本条款及相关政策。\n\n如果你不同意本条款，请停止使用本应用。'
    ,
    'disclosure.title': '数据使用说明',
    'disclosure.body': '首次使用前，请确认你已了解本应用的数据处理方式。',
    'disclosure.local_data': '我已了解：我的日志与设置主要保存在本设备本地。',
    'disclosure.cloud_backup': '我已了解：云备份是可选功能，并将加密文件存到我自己的云账户。',
    'disclosure.billing': '我已了解：若购买订阅，支付与权益由 Google Play 和 RevenueCat 处理。',
    'disclosure.personalized_ads': '允许个性化广告（如可用，可选）。',
    'disclosure.continue': '保存并继续',
    'disclosure.exit': '退出应用',
    'disclosure.required_hint': '请先确认所有必选项后再继续。',
    'adsconsent.title': '个性化广告同意',
    'adsconsent.body': '在 EEA/英国展示个性化广告前，我们需要你的同意。你也可以继续使用非个性化广告，并在“选项”中稍后更改。',
    'adsconsent.allow': '同意个性化广告',
    'adsconsent.keep_non_personalized': '保持非个性化广告'
  },
  ja: {
    'app.title': 'Rethread',
    'topbar.park': '思考を退避',
    'topbar.options': 'オプション',
    'tab.log': '記録',
    'tab.rescue': 'レスキュー',
    'tab.history': '履歴',
    'tab.insights': '分析',
    'tab.options': 'オプション',
    'options.theme': 'テーマ',
    'options.style_language': 'スタイル言語',
    'options.visual_theme': 'カラーテーマ',
    'options.typography': 'フォント',
    'options.app_font': 'アプリフォント',
    'options.language': '言語',
    'options.appearance': '表示',
    'options.compact': 'コンパクト表示',
    'options.simplified_mode': 'シンプル記録モード',
    'options.tips': 'ヒントを表示',
    'options.rescue_defaults': 'レスキュー既定',
    'options.sound_default': '集中タイマー終了音を既定で有効',
    'options.notifications': '通知',
    'options.notifications_desc':
      '進行中のレスキュー外で使う静かなリマインダーです。時間つきレスキューを始めたときの終了通知はそのまま届きます。',
    'options.notification_style': 'リマインダーの強さ',
    'options.notification_style_off': 'オフ',
    'options.notification_style_quiet': '静か',
    'options.notification_style_standard': '標準',
    'options.notification_exact_alarm': '正確なタイマー通知',
    'options.notification_exact_alarm_granted': '許可済み',
    'options.notification_exact_alarm_denied': 'Android設定で無効です。バックグラウンド通知が遅れることがあります。',
    'options.notification_exact_alarm_unknown': 'この端末では不明',
    'options.notification_exact_alarm_open_settings': '正確なアラーム設定を開く',
    'options.notification_android_battery_note':
      'バックグラウンドのタイマー通知がまだ遅れる場合は、Android設定でこのアプリの電池使用を制限なしにするか、電池最適化をオフにしてください。',
    'options.notification_android_battery_warning':
      'Androidがバックグラウンドのタイマー通知を遅らせている可能性があります。時間どおりに必要なら、このアプリで正確なアラームを有効にし、電池制限を外してください。',
    'options.reentry_nudges': 'レスキュー後のやさしい再開リマインダー',
    'options.daily_reflection_prompt': '夜のふり返りリマインダー',
    'options.weekly_pattern_prompt': '週ごとのパターン確認リマインダー',
    'options.safety': '安全',
    'options.confirm_delete': '削除前に確認',
    'options.monetization': 'マネタイズ',
    'options.ads_enabled': '集中以外の画面で広告を表示',
    'options.personalized_ads': 'パーソナライズ広告を使用（対応地域）',
    'options.ad_consent_status': 'パーソナライズ広告の同意状態',
    'options.ad_consent_unknown': '未設定',
    'options.ad_consent_accepted': '同意済み',
    'options.ad_consent_rejected': '拒否',
    'options.ad_consent_not_required': '地域要件なし',
    'options.withdraw_ad_consent': '広告パーソナライズ同意を撤回',
    'options.ad_privacy_options': '広告プライバシー設定を開く',
    'options.ads_cmp_notice': 'EEA/英国のユーザー向けには、パーソナライズ広告に認証済み同意フローが必要です。',
    'options.ad_consent_withdrawn': '広告パーソナライズの同意を撤回しました。',
    'options.ad_consent_already_off': '広告パーソナライズはすでに無効です。',
    'options.ad_privacy_options_opened': '広告プライバシー設定を開きました。',
    'options.ad_privacy_options_unavailable': 'この端末では広告プライバシー設定をまだ利用できません。',
    'options.ad_privacy_options_failed': '広告プライバシー設定を開けませんでした。',
    'options.ad_consent_sync_failed': '広告同意状態を更新できませんでした。',
    'options.billing_desc': 'この端末のプランは RevenueCat のエンタイトルメントで判定します。',
    'options.billing_configured': 'RevenueCat は設定済みです。',
    'options.billing_not_configured': 'RevenueCat は未設定です。',
    'options.billing_status': '課金ステータス',
    'options.billing_tier': '現在のプラン',
    'options.billing_tier_free': '無料',
    'options.billing_tier_ad_free': '広告なし',
    'options.billing_tier_pro': 'Pro',
    'options.dev_unlock_pro': 'この端末で一時的に Pro を解放',
    'options.dev_unlock_pro_hint': '有料画面確認用のローカルプレビュー切り替えです。',
    'options.billing_pro_benefits_title': 'Proで使える機能',
    'options.billing_pro_benefits_body': '高度な分析内訳、シーケンス、シグナル分析、CSVエクスポート。',
    'options.billing_check': '購入を確認',
    'options.billing_restore': '購入を復元',
    'options.billing_disclosure':
      'サブスクは解約するまで自動更新されます。課金は Google Play が処理します。管理・解約は Google Play の定期購入から行えます。',
    'options.billing_plans': '購入可能プラン',
    'options.billing_no_plans': '現在購入可能なプランはありません。',
    'options.billing_buy': '購入',
    'options.billing_current': '現在のプラン',
    'options.billing_manage': 'サブスク管理',
    'options.billing_manage_unavailable': 'サブスク管理リンクはまだ利用できません。',
    'options.billing_check_done': '購入確認が完了しました。',
    'options.billing_check_failed': '購入確認に失敗しました。',
    'options.billing_restore_done': '復元が完了しました。',
    'options.billing_restore_failed': '復元に失敗しました。',
    'options.billing_buy_done': '購入が完了しました。',
    'options.billing_buy_failed': '購入に失敗したかキャンセルされました。',
    'options.billing_manage_opened': 'サブスク管理を開きました。',
    'options.billing_diagnostics_title': '課金診断',
    'options.billing_diagnostics_run': '課金診断を実行',
    'options.billing_diagnostics_clear_events': 'イベントログを消去',
    'options.billing_diagnostics_done': '課金診断が完了しました。',
    'options.billing_diagnostics_failed': '課金診断に失敗しました。',
    'options.billing_diagnostics_not_available': '利用不可',
    'options.billing_diagnostics_last_check': '最終チェック',
    'options.billing_diagnostics_checks': '準備チェック',
    'options.billing_diagnostics_check_ok': 'OK',
    'options.billing_diagnostics_check_needs_action': '要対応',
    'options.billing_diagnostics_details': '詳細',
    'options.billing_diagnostics_app_user_id': 'アプリユーザーID',
    'options.billing_diagnostics_offering': '現在のオファリング',
    'options.billing_diagnostics_packages': '検出パッケージ数',
    'options.billing_diagnostics_entitlements': '有効なエンタイトルメント',
    'options.billing_diagnostics_subscriptions': '有効なサブスク',
    'options.billing_diagnostics_management_url': '管理リンク',
    'options.billing_diagnostics_recent_events': '最近の収益化イベント',
    'options.billing_diagnostics_no_events': '収益化イベントはまだありません。',
    'options.backup': 'バックアップと同期',
    'options.backup_desc': 'データはローカル優先のまま、必要なら暗号化バックアップをクラウドに保存できます。',
    'options.local_first_title': 'Webではローカル優先',
    'options.local_first_body': 'Web版の Rethread は、このブラウザ内にデータを保存します。アカウントやサインインは不要です。',
    'options.local_first_risk':
      'ブラウザデータを削除したり端末を替えたりすると、エクスポートや暗号化バックアップをしていない限りローカルデータが失われることがあります。',
    'options.backup_passphrase': 'バックアップ用パスフレーズ',
    'options.backup_create': '暗号化バックアップを作成',
    'options.backup_restore': 'バックアップファイルを復元',
    'options.backup_status': 'バックアップ状態',
    'options.backup_done': '暗号化バックアップをダウンロードしました。',
    'options.backup_failed': 'バックアップに失敗しました。',
    'options.restore_done': 'バックアップを復元しました。',
    'options.restore_failed': '復元に失敗しました。',
    'options.sync_in_progress': '処理中',
    'options.sync_working_action': '{action}...',
    'options.drive': 'Google Drive',
    'options.drive_desc': '暗号化バックアップを Google Drive のアプリデータフォルダに同期します。',
    'options.drive_connect': 'Google Drive に接続',
    'options.drive_disconnect': 'Google Drive から切断',
    'options.drive_upload': '暗号化バックアップをアップロード',
    'options.drive_restore': 'Drive から最新を復元',
    'options.drive_configured': 'Google Drive クライアント設定済み。',
    'options.drive_not_configured': 'このプラットフォームでは Google Drive が未設定です。',
    'options.drive_connected': '接続状態: 接続済み',
    'options.drive_not_connected': '接続状態: 未接続',
    'options.drive_connected_status': 'Google Drive に接続しました。',
    'options.drive_disconnected_status': 'Google Drive から切断しました。',
    'options.drive_connect_failed': 'Google Drive への接続に失敗しました。',
    'options.drive_upload_done': '暗号化バックアップを Google Drive にアップロードしました。',
    'options.drive_upload_failed': 'Google Drive へのアップロードに失敗しました。',
    'options.drive_restore_done': 'Google Drive からバックアップを復元しました。',
    'options.drive_restore_failed': 'Google Drive からの復元に失敗しました。',
    'options.help': 'ヘルプ',
    'options.replay_title': '初回オンボーディングを再生',
    'options.replay_desc': '導入ガイドをもう一度表示します。',
    'options.replay_btn': 'オンボーディング再生',
    'options.privacy_data': 'プライバシーとデータ',
    'options.privacy_desc': 'ポリシー確認、サポート連絡、データ書き出し、この端末上の全データ削除ができます。',
    'options.privacy_rights_sla': 'プライバシー請求はサポートメールで受け付け、通常30日以内に対応します。',
    'options.children_notice': '本アプリは13歳未満（または各地域の最低デジタル年齢）を対象としていません。',
    'options.privacy_policy': 'プライバシーポリシー',
    'options.terms': '利用規約',
    'options.contact_support': 'サポートに連絡',
    'options.privacy_export_data': 'データをエクスポート（JSON）',
    'options.privacy_delete_data': 'この端末の全データを削除',
    'options.privacy_export_done': '個人データの書き出しを保存しました。',
    'options.privacy_delete_confirm': 'この端末のローカルデータをすべて削除しますか？この操作は元に戻せません。',
    'options.privacy_delete_done': 'ローカルデータをすべて削除しました。',
    'options.privacy_delete_failed': 'ローカルデータの削除に失敗しました。',
    'log.tip': 'ヒント: 集中が外れたと気づいたら、すぐに停滞タイマーを開始。',
    'log.flow_title': '今いちばん近いのはどれですか？',
    'log.flow_body': 'カードを1つ選びます。詳細やレスキュー開始はそのあとで大丈夫です。',
    'log.flow_primary': '1. 今の状態を記録',
    'log.flow_secondary': '2. 必要なら補助を使う',
    'log.flow_timer_hint': '停滞タイマー:',
    'log.start_stuck': '停滞タイマー開始',
    'log.undo': '元に戻す',
    'log.stuck_prefix': '停滞:',
    'log.how_it_works': 'やさしく戻る流れ:',
    'log.slip_helper': '集中が外れたと気づいたら開始。次の記録に停滞時間が入ります。',
    'log.quick_sheet_title': 'クイックサポート',
    'log.quick_sheet_kicker': '今、引っかかりに気づけました',
    'log.quick_sheet_body': 'まずは小さな一歩だけ。',
    'log.quick_primary_title': 'まずはここから',
    'log.quick_primary_body': 'このあと数分を少しやりやすくするための一歩です。',
    'log.quick_optional': 'もう少し合う形にする（任意）',
    'log.quick_sheet_skip': '今はこれを残しておく',
    'log.quick_top_pick': 'おすすめの支え',
    'log.quick_other_rescues': '他の選択肢',
    'log.simplified_badge': 'シンプルモード',
    'log.simplified_hint': 'カードを1つ選び、いちばん始めやすい支えから始めます。',
    'rescue.timer_done': 'レスキュータイマー完了',
    'rescue.pause': '一時停止',
    'rescue.resume': '再開',
    'rescue.complete': '完了',
    'rescue.snooze': '2分スヌーズ',
    'rescue.cancel': 'キャンセル',
    'rescue.what_helped': '少しでも助けになったことは？',
    'rescue.followup_optional': '任意です。ひとつ選ぶだけで十分です。',
    'rescue.outcome_helped': '助けになった',
    'rescue.outcome_a_bit': '少し',
    'rescue.outcome_not_really': 'あまり',
    'rescue.repeat_10m': 'さらに10分やる',
    'rescue.repeat_minutes': 'さらに{minutes}分やる',
    'rescue.log_tiny_win': '助けになったことを残す',
    'rescue.skip': '今はスキップ',
    'rescue.tiny_win_saved': '小さな成果を保存しました。',
    'rescue.tip': 'ヒント: 小さなレスキューや支えを1つ選んで、そこから始めます。細かい調整はあとでできます。',
    'rescueguide.reset_2m_summary': '立ち上がって力を抜き、画面から目を離して、水をひと口飲みます。',
    'rescueguide.make_smaller_10m_summary': 'いちばん小さくて見える次の一歩を決めて、それだけを10分やります。',
    'rescueguide.focus_25m_summary': 'やることを1つに絞り、境界はタイマーに任せます。',
    'rescueguide.breathe_90s_summary': 'ボックス呼吸で少し落ち着いてから続けます。',
    'rescueguide.brain_dump_2m_summary': '頭の中の渦をまずここに書き出します。断片で大丈夫です。',
    'rescueguide.walk_5m_summary': '数分だけ体を動かして、少し圧を下げてから戻ります。',
    'rescueguide.stand_water_summary': '立って水を飲みます。目的は素早い身体リセットです。',
    'rescueguide.one_sentence_summary': '流れを戻すために、まず荒く1文だけ書きます。',
    'rescueguide.make_smaller_prompt': 'いちばん小さくて見える次の一歩は？',
    'rescueguide.brain_dump_prompt': '今、頭を埋めているものは何ですか？',
    'rescueguide.one_sentence_prompt': 'ここに1文だけ書く',
    'rescueguide.make_smaller_action': 'この一歩で10分始める',
    'rescueguide.brain_dump_action': '2分の書き出しを始める',
    'rescueguide.one_sentence_action': 'この1文を保存',
    'rescueguide.breathe_intro': '四角に合わせて呼吸します。',
    'rescueguide.breathe_in': '吸う',
    'rescueguide.breathe_hold': '止める',
    'rescueguide.breathe_out': '吐く',
    'notifications.reentry_title': '戻れそうですか？',
    'notifications.reentry_body': '次の一歩をひとつだけ試します。きれいにやる必要はありません。',
    'notifications.daily_title': '少しだけ振り返りますか？',
    'notifications.daily_body': '今日がぼやける前に 20 秒だけ見返します。',
    'notifications.weekly_title': '今週にもパターンがあります',
    'notifications.weekly_body': '助けになったことと引っかかったことを軽く見返します。',
    'common.saved': '保存済み',
    'common.close': '閉じる',
    'common.cancel': 'キャンセル',
    'common.none': 'なし',
    'common.na': '該当なし',
    'rescuepicker.favorites': 'お気に入り',
    'rescuepicker.recent_context_hint': '最近の似た場面で保存した内容',
    'rescuepicker.apply_last_context': '前回の内容を使う',
    'rescuepicker.favorite_on': 'お気に入り固定を外す',
    'rescuepicker.favorite_off': 'お気に入りに固定',
    'rescuepicker.context_step': '任意の状況メモ',
    'rescuepicker.context_hint': 'それで支えが少し合いやすくなるときだけ開いてください。',
    'rescuepicker.rescue_step': 'まずは支えから',
    'rescuepicker.rescue_hint': 'いちばん始めやすいレスキューや支えを選びます。',
    'rescuepicker.support_step': '始めやすくする（任意）',
    'rescuepicker.support_hint': '始めやすくなるなら使ってください。',
    'parking.title': '思考の退避',
    'parking.desc': '考えを退避して、作業に戻りましょう。',
    'parking.placeholder': '退避する思考を書いてください',
    'parking.save': '保存',
    'onboarding.welcome': 'ようこそ',
    'onboarding.skip': 'スキップ',
    'onboarding.step': 'ステップ {current}/{total}',
    'onboarding.back': '戻る',
    'onboarding.next': '次へ',
    'onboarding.start': '記録を開始',
    'history.empty': 'この条件ではまだ何もありません。摩擦の瞬間に気づいたら、ここに残ります。',
    'history.rescue_used': 'レスキュー使用',
    'insights.empty': 'ここにはまだ読むものがあまりありません。いくつかの摩擦の瞬間と支えがたまると、少しずつパターンが見えてきます。',
    'insights.today_snapshot': '今日の概要',
    'insights.pings': '記録数',
    'insights.recovered': '回復',
    'insights.avg_stuck': '平均停滞',
    'insights.patterns': 'パターン',
    'insights.calendar_view': 'カレンダービュー',
    'insights.swipe_hint': '左右にスワイプして全時間帯を表示。',
    'insights.top_types': '摩擦のパターン',
    'insights.sequences': 'そのあと起きやすいこと',
    'insights.median_gap': '中央値間隔',
    'insights.what_helps': '摩擦をやわらげやすいもの',
    'insights.felt_helpful': '役に立った実感',
    'insights.outcomes_logged': '結果記録数',
    'insights.small_sample': 'サンプル小',
    'insights.started': '開始数',
    'insights.completion': '完了率',
    'insights.resolved': '改善率',
    'insights.median_next_ping': '次の摩擦までの目安時間',
    'insights.associated_hint': 'このあとは追加の摩擦が少なめになりやすいです。',
    'insights.interruption_sources': '負担を増やしやすいもの',
    'insights.no_interruptions': '中断タグはまだありません。',
    'insights.friction_subtypes': '摩擦の種類',
    'insights.no_subtypes': 'サブタイプタグはまだありません。',
    'insights.tiny_wins': '小さな成果',
    'insights.captured': '記録数',
    'insights.completed_rescues': '完了レスキュー',
    'insights.capture_rate': '記録率',
    'insights.latest': '最新',
    'insights.none_yet': 'まだなし',
    'insights.prep_checklist': '準備チェック',
    'insights.timed_rescues': '時間付きレスキュー',
    'insights.phone_silenced': 'スマホ消音',
    'insights.tabs_closed': 'タブを閉じた',
    'insights.doc_open': '対象ドキュメントを開いた',
    'insights.timer_visible': 'タイマー表示',
    'insights.friction_start': '停滞開始',
    'insights.pings_slipping_timer': '停滞タイマー付き記録',
    'insights.median_stuck_before_ping': '記録前停滞の中央値',
    'insights.mean_stuck_before_ping': '記録前停滞の平均',
    'insights.parking_lot': '思考の退避',
    'insights.no_parked': '退避した思考はまだありません。',
    'insights.export': 'エクスポート',
    'insights.export_json': 'JSONを出力',
    'insights.export_csv': 'CSVを出力',
    'insights.sections': 'セクション',
    'insights.expand_all': 'すべて展開',
    'insights.collapse_all': 'すべて折りたたむ',
    'insights.day_breakdown': '日別内訳',
    'insights.no_day_data': 'データなし',
    'insights.selected_day': '選択日',
    'insights.rescues_started': '開始レスキュー',
    'insights.top_rescues': '上位レスキュー',
    'insights.hour_pattern': '時間帯パターン',
    'insights.prev_month': '前の月',
    'insights.next_month': '次の月',
    'insights.event_details': 'イベント詳細',
    'insights.starts_at': '開始',
    'insights.ends_at': '終了',
    'insights.duration': '所要時間',
    'insights.status': '状態',
    'insights.pro_locked_title': 'Pro 機能',
    'insights.pro_locked_body': 'オプションで Pro にアップグレードすると詳細分析を利用できます。',
    'insights.pro_upgrade_cta': 'オプションでアップグレード',
    'rescuepicker.whats_happening': '今どうなっていますか？',
    'rescuepicker.pulled_away': '何が気をそらしましたか？',
    'rescuepicker.choose_rescue': 'レスキューを選択',
    'rescuepicker.start': '開始',
    'rescuepicker.prep_checklist': '準備チェック（時間付きレスキュー）',
    'rescuepicker.reset_tip': '2分リセット: 立ち上がって、ゆっくり呼吸し、可能なら水を飲む。',
    'rescuepicker.optional_details': '任意の詳細',
    'rescuepicker.smallest_step': '最小の次の一歩は？',
    'rescuepicker.start_10m_note': 'メモ付きで10分開始',
    'rescuepicker.sentence_placeholder': '1文書く',
    'rescuepicker.save_sentence': '文を保存',
    'rescuepicker.focus_sound': '集中タイマー終了時に音を鳴らす',
    'rescuepicker.edit_text': 'レスキューテキストを編集',
    'rescuepicker.save_script': '保存',
    'detail.title': '記録の詳細',
    'detail.type': 'タイプ',
    'detail.stuck_before_ping': '記録前の停滞',
    'detail.subtype': 'サブタイプ',
    'detail.interrupted_by': '中断要因',
    'detail.completed': '完了',
    'detail.canceled': 'キャンセル',
    'detail.tiny_win': '小さな成果',
    'detail.prep': '準備',
    'detail.phone': 'スマホ',
    'detail.tabs': 'タブ',
    'detail.doc': 'ドキュメント',
    'detail.timer': 'タイマー',
    'detail.add_note': 'メモ追加',
    'detail.save_note': 'メモ保存',
    'detail.delete_ping': '記録を削除',
    'app.storage_unavailable': 'ストレージを利用できません',
    'app.confirm_delete_ping': 'この記録を削除しますか？',
    'legal.title': 'プライバシーと利用規約',
    'legal.body': '続行する前に、プライバシーポリシーと利用規約を確認して同意してください。',
    'legal.disclaimer':
      'Rethread は生産性サポートツールです。医療機器ではなく、診断や治療を提供するものではありません。',
    'legal.accept': '同意して続行',
    'legal.exit': 'アプリを終了',
    'legal.doc_title_privacy': 'プライバシーポリシー',
    'legal.doc_title_terms': '利用規約',
    'legal.doc_open_external': 'Web版（全文）を開く',
    'legal.doc_last_updated': '最終更新日: 2026年3月3日',
    'legal.privacy_text':
      'このアプリは、ログ、レスキュー履歴、設定、退避した思考を主に端末内に保存します。\n\nバックアップを有効にすると、暗号化バックアップはご自身のクラウドアカウント（例: Google Drive のアプリデータ）に保存されます。\n\nサブスクリプションが有効な場合、Google Play と RevenueCat が購入・エンタイトルメント情報を処理します。\n\n広告が有効な場合、広告事業者が端末情報や広告操作情報を各社ポリシーに基づき処理することがあります。EEA/英国では、パーソナライズ広告は同意後のみ表示され、オプションから撤回できます。\n\nデータのエクスポートと端末内データの全削除は、オプション -> プライバシーとデータから実行できます。\n\nRethread は生産性支援ツールであり、医療アドバイス・診断・治療を提供しません。',
    'legal.terms_text':
      'Rethread は生産性支援およびセルフマネジメント目的で現状有姿で提供されます。\n\nアプリ利用、端末の安全管理、バックアップ用パスフレーズ管理は利用者の責任です。\n\n有料サブスクリプションを提供する場合があります。課金、更新、解約はアプリストア側で管理されます。\n\n法令違反行為、サービスの悪用、有料機能の回避を目的とした行為は禁止します。\n\n機能は将来変更される場合があり、本規約や関連ポリシーを更新することがあります。\n\n本規約に同意できない場合は、アプリの利用を中止してください。'
    ,
    'disclosure.title': 'データ利用の開示',
    'disclosure.body': '初回利用前に、このアプリでのデータ取り扱いについて確認してください。',
    'disclosure.local_data': 'ログと設定がこの端末にローカル保存されることを理解しました。',
    'disclosure.cloud_backup': 'クラウドバックアップは任意で、暗号化ファイルが自分のクラウドに保存されることを理解しました。',
    'disclosure.billing': 'プラン購入時、サブスク処理は Google Play と RevenueCat で行われることを理解しました。',
    'disclosure.personalized_ads': '可能な場合、パーソナライズ広告を許可する（任意）。',
    'disclosure.continue': '保存して続行',
    'disclosure.exit': 'アプリを終了',
    'disclosure.required_hint': '続行するには必須項目をすべて確認してください。',
    'adsconsent.title': 'パーソナライズ広告への同意',
    'adsconsent.body':
      'EEA/英国でパーソナライズ広告を表示するには、先に同意が必要です。非パーソナライズ広告のまま利用し、後でオプションから変更できます。',
    'adsconsent.allow': 'パーソナライズ広告を許可',
    'adsconsent.keep_non_personalized': '非パーソナライズ広告のままにする'
  }
};

function fallbackKey(key: string): string {
  return I18N.en[key] ?? key;
}

export function t(language: AppLanguage, key: string, vars?: Record<string, string | number>): string {
  const template = I18N[language][key] ?? fallbackKey(key);
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, name) => String(vars[name] ?? ''));
}

const FR_TYPE: Record<PingType, Record<AppLanguage, string>> = {
  cant_start: { en: "Can't Start", es: 'No puedo empezar', zh: '无法开始', ja: '始められない' },
  stuck_mid_task: { en: 'Stuck Mid-Task', es: 'Atascado a mitad', zh: '中途卡住', ja: '途中で止まる' },
  avoiding: { en: 'Avoiding', es: 'Evitando', zh: '回避中', ja: '回避' },
  overwhelmed: { en: 'Overwhelmed', es: 'Abrumado', zh: '不堪重负', ja: '圧倒される' },
  distracted: { en: 'Distracted / Scrolling', es: 'Distraído / Scrolling', zh: '分心 / 刷屏', ja: '気が散る / スクロール' },
  too_wired: { en: 'Too Wired', es: 'Demasiado acelerado', zh: '过度兴奋', ja: '冴えすぎ' },
  too_tired: { en: 'Too Tired', es: 'Demasiado cansado', zh: '太累了', ja: '疲れすぎ' },
  ruminating: { en: 'Ruminating', es: 'Rumiando', zh: '反刍思考', ja: '反すう' },
  irritable: { en: 'Irritable', es: 'Irritable', zh: '烦躁', ja: 'イライラ' }
};

const RESCUE_KIND: Record<RescueKind, Record<AppLanguage, string>> = {
  reset_2m: { en: '2-min reset', es: 'Reset 2 min', zh: '2分钟重置', ja: '2分リセット' },
  make_smaller_10m: { en: '10-min make it smaller', es: '10 min hacerlo más pequeño', zh: '10分钟缩小一步', ja: '10分で小さく進める' },
  focus_25m: { en: '25-min focus sprint', es: 'Sprint de foco 25 min', zh: '25分钟专注冲刺', ja: '25分集中スプリント' },
  breathe_90s: { en: '90-sec breathing reset', es: 'Reseteo de respiración (90 s)', zh: '90秒呼吸重置', ja: '90秒呼吸リセット' },
  brain_dump_2m: { en: '2-min brain dump', es: 'Descarga mental de 2 min', zh: '2分钟脑内清空', ja: '2分ブレインダンプ' },
  walk_5m: { en: '5-min walk', es: 'Caminata de 5 min', zh: '5分钟走一走', ja: '5分ウォーク' },
  stand_water: { en: 'Stand + water', es: 'Pararse + agua', zh: '起身 + 喝水', ja: '立つ + 水を飲む' },
  one_sentence: { en: 'Write 1 sentence', es: 'Escribir 1 frase', zh: '写一句话', ja: '1文書く' }
};

const INTERRUPTION: Record<InterruptionSource, Record<AppLanguage, string>> = {
  notification: { en: 'Notification', es: 'Notificación', zh: '通知', ja: '通知' },
  person: { en: 'Person', es: 'Persona', zh: '他人', ja: '人' },
  tab_drift: { en: 'Tab drift', es: 'Deriva de pestañas', zh: '标签页漂移', ja: 'タブ逸脱' },
  thought_loop: { en: 'Thought loop', es: 'Bucle mental', zh: '思维循环', ja: '思考ループ' },
  unknown: { en: 'Unknown', es: 'Desconocido', zh: '未知', ja: '不明' }
};

const RANGE: Record<DateRange, Record<AppLanguage, string>> = {
  today: { en: 'today', es: 'hoy', zh: '今天', ja: '今日' },
  '7d': { en: '7d', es: '7d', zh: '7天', ja: '7日' },
  '30d': { en: '30d', es: '30d', zh: '30天', ja: '30日' },
  '90d': { en: '90d', es: '90d', zh: '90天', ja: '90日' },
  all: { en: 'all', es: 'todo', zh: '全部', ja: 'すべて' }
};

export function frictionTypeLabel(language: AppLanguage, type: PingType): string {
  return FR_TYPE[type][language] ?? FR_TYPE[type].en;
}

export function rescueKindLabel(language: AppLanguage, kind: RescueKind): string {
  return RESCUE_KIND[kind][language] ?? RESCUE_KIND[kind].en;
}

export function interruptionLabel(language: AppLanguage, source: InterruptionSource): string {
  return INTERRUPTION[source][language] ?? INTERRUPTION[source].en;
}

export function rangeLabel(language: AppLanguage, range: DateRange): string {
  return RANGE[range][language] ?? RANGE[range].en;
}

export function dayLabel(language: AppLanguage, day: 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat' | 'Sun'): string {
  const days: Record<AppLanguage, Record<'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat' | 'Sun', string>> = {
    en: { Mon: 'Mon', Tue: 'Tue', Wed: 'Wed', Thu: 'Thu', Fri: 'Fri', Sat: 'Sat', Sun: 'Sun' },
    es: { Mon: 'Lun', Tue: 'Mar', Wed: 'Mié', Thu: 'Jue', Fri: 'Vie', Sat: 'Sáb', Sun: 'Dom' },
    zh: { Mon: '一', Tue: '二', Wed: '三', Thu: '四', Fri: '五', Sat: '六', Sun: '日' },
    ja: { Mon: '月', Tue: '火', Wed: '水', Thu: '木', Fri: '金', Sat: '土', Sun: '日' }
  };
  return days[language][day] ?? days.en[day];
}

const THEME_DESCRIPTIONS: Record<AppTheme, Record<AppLanguage, string>> = {
  dracula: {
    en: 'Classic dark purple palette with vivid accents.',
    es: 'Paleta oscura púrpura clásica con acentos vivos.',
    zh: '经典暗紫配色，强调色更鲜明。',
    ja: '鮮やかなアクセントを持つクラシックなダークパープル配色。'
  },
  monokai: {
    en: 'Dark terminal-inspired palette with bold color cues.',
    es: 'Paleta oscura inspirada en terminal con colores marcados.',
    zh: '终端风深色主题，颜色提示更醒目。',
    ja: 'ターミナル風のダーク配色で色の手がかりが明確。'
  },
  solarized_light: {
    en: 'Low-contrast light palette for long sessions.',
    es: 'Paleta clara de bajo contraste para sesiones largas.',
    zh: '低对比浅色主题，适合长时间使用。',
    ja: '長時間利用向けの低コントラストライト配色。'
  },
  solarized_dark: {
    en: 'Balanced dark palette tuned for readability.',
    es: 'Paleta oscura equilibrada, ajustada para legibilidad.',
    zh: '均衡的深色主题，阅读性更好。',
    ja: '可読性を重視したバランスの良いダーク配色。'
  },
  calm_mint: {
    en: 'Soft greens and low visual intensity.',
    es: 'Verdes suaves y baja intensidad visual.',
    zh: '柔和薄荷绿，视觉刺激更低。',
    ja: 'やわらかなグリーンで視覚刺激を抑えた配色。'
  },
  warm_paper: {
    en: 'Warm neutrals with softer contrast.',
    es: 'Neutros cálidos con contraste más suave.',
    zh: '温暖中性色，整体对比更柔和。',
    ja: '暖色ニュートラルでコントラストをやわらかく。'
  },
  high_contrast: {
    en: 'Sharper contrast for quick scanning.',
    es: 'Contraste más fuerte para escaneo rápido.',
    zh: '更高对比，便于快速扫读。',
    ja: '素早く見渡しやすい高コントラスト配色。'
  },
  sunrise_peach: {
    en: 'Warm peach tones with gentle contrast.',
    es: 'Tonos durazno cálidos con contraste suave.',
    zh: '暖桃色调，柔和对比。',
    ja: '温かいピーチトーンのやさしいコントラスト。'
  },
  ocean_breeze: {
    en: 'Cool blue-greens with calm readability.',
    es: 'Azules verdosos frescos con lectura tranquila.',
    zh: '清爽蓝绿色，阅读体验更平静。',
    ja: '落ち着いて読めるクールなブルーグリーン配色。'
  },
  slate_focus: {
    en: 'Neutral slate palette for low distraction.',
    es: 'Paleta pizarra neutra para baja distracción.',
    zh: '中性石板色，减少干扰。',
    ja: '気が散りにくいニュートラルなスレート配色。'
  }
};

const FONT_DESCRIPTIONS: Record<AppFont, Record<AppLanguage, string>> = {
  atkinson: {
    en: 'Accessibility-focused default with clear letterforms.',
    es: 'Fuente por defecto enfocada en accesibilidad, con letras bien claras.',
    zh: '以可访问性为先的默认字体，字形清晰。',
    ja: '視認性を重視した標準フォント。'
  },
  system: {
    en: 'Use the native platform font stack.',
    es: 'Usa la tipografía nativa de tu dispositivo.',
    zh: '使用设备原生系统字体。',
    ja: 'デバイス標準のフォントを使用。'
  },
  lexend: {
    en: 'Wider spacing designed to ease visual tracking.',
    es: 'Espaciado más amplio para facilitar el seguimiento visual.',
    zh: '更宽的字距，便于视觉跟踪。',
    ja: '文字追従を助ける広めの字間。'
  },
  source_sans: {
    en: 'Neutral sans-serif with calm rhythm.',
    es: 'Sans serif neutra, con ritmo visual tranquilo.',
    zh: '中性无衬线，阅读节奏平稳。',
    ja: '落ち着いた読み心地のニュートラルサンセリフ。'
  },
  nunito: {
    en: 'Rounded sans-serif with a softer tone.',
    es: 'Sans serif redondeada, más amable visualmente.',
    zh: '圆润无衬线，视觉更柔和。',
    ja: 'やわらかい印象の丸みあるサンセリフ。'
  },
  ibm_plex: {
    en: 'Structured and technical with strong readability.',
    es: 'Estructurada y técnica, con muy buena legibilidad.',
    zh: '结构感更强，技术风且可读性高。',
    ja: '構造的で読みやすいテクニカル寄りの書体。'
  }
};

const VISUAL_STYLE_DESCRIPTIONS: Record<AppVisualStyle, Record<AppLanguage, string>> = {
  soft_tactile: {
    en: 'Soft surfaces, cushioned controls, and gentle depth for a grounded recovery feel.',
    es: 'Superficies suaves, controles acolchados y profundidad leve para una sensación de apoyo.',
    zh: '柔和表面、缓冲式控件和轻微层次，带来更安定的支持感。',
    ja: 'やわらかな面、クッション感のある操作要素、穏やかな奥行きで落ち着いた回復感。'
  },
  paper_panel: {
    en: 'Flatter paper-like panels, quieter tabs, and clearer page structure with less chrome.',
    es: 'Paneles más planos, tipo papel, pestañas discretas y una estructura más clara con menos adorno.',
    zh: '更平整的纸张感面板、更安静的标签和更清晰的页面结构，减少装饰感。',
    ja: '紙のようにフラットな面、控えめなタブ、装飾を抑えた明快なページ構成。'
  },
  tactile_instrument: {
    en: 'Framed modules, inset wells, rails, and calmer control surfaces that feel like a focused tool.',
    es: 'Módulos enmarcados, cavidades internas, rieles y superficies de control más serenas que se sienten como una herramienta.',
    zh: '带框模块、内凹区域、导轨和更克制的控制表面，像一件专注使用的工具。',
    ja: '枠付きモジュール、沈み込み面、レール状の構成で、落ち着いた道具らしさを感じるスタイル。'
  },
  comic_board: {
    en: 'Bold ink outlines, paper-white cards, offset shadows, and a graphic board feel.',
    es: 'Contornos entintados, tarjetas claras, sombras desplazadas y una presencia más gráfica.',
    zh: '更粗的描边、纸感卡片、偏移阴影和更强的图像化看板气质。',
    ja: '太めの輪郭線、紙のようなカード、ずらした影でグラフィックなボード感を出すスタイル。'
  }
};

const SUBTYPE_LABELS: Record<string, Record<AppLanguage, string>> = {
  cant_start_too_big: { en: 'Too big', es: 'Demasiado grande', zh: '任务太大', ja: '大きすぎる' },
  cant_start_unclear: { en: 'Unclear next step', es: 'Siguiente paso poco claro', zh: '下一步不清楚', ja: '次の一歩が不明確' },
  cant_start_perfectionism: { en: 'Perfectionism', es: 'Perfeccionismo', zh: '完美主义', ja: '完璧主義' },
  cant_start_low_energy: { en: 'No energy', es: 'Sin energía', zh: '没精力', ja: 'エネルギー不足' },
  stuck_mid_task_hard_resume: { en: 'Hard to resume', es: 'Difícil retomar', zh: '难以重新开始', ja: '再開しづらい' },
  stuck_mid_task_lost_thread: { en: 'Lost the thread', es: 'Perdí el hilo', zh: '思路断了', ja: '流れを見失った' },
  stuck_mid_task_too_many_tabs: { en: 'Too many tabs', es: 'Demasiadas pestañas', zh: '标签页太多', ja: 'タブが多すぎる' },
  stuck_mid_task_blocked_detail: { en: 'Blocked by detail', es: 'Bloqueado por detalle', zh: '被细节卡住', ja: '細部で詰まる' },
  avoiding_task_aversion: { en: 'Task aversion', es: 'Rechazo a la tarea', zh: '回避任务', ja: '課題回避' },
  avoiding_fear_of_mistake: { en: 'Fear of mistake', es: 'Miedo a equivocarme', zh: '害怕犯错', ja: 'ミスへの不安' },
  avoiding_bored: { en: 'Bored', es: 'Aburrido', zh: '无聊', ja: '退屈' },
  avoiding_unclear_next_step: { en: 'Unclear next step', es: 'Siguiente paso poco claro', zh: '下一步不清楚', ja: '次の一歩が不明確' },
  overwhelmed_too_many_tasks: { en: 'Too many tasks', es: 'Demasiadas tareas', zh: '任务太多', ja: 'タスクが多すぎる' },
  overwhelmed_no_priority: { en: 'No priority', es: 'Sin prioridad', zh: '没有优先级', ja: '優先順位がない' },
  overwhelmed_time_pressure: { en: 'Time pressure', es: 'Presión de tiempo', zh: '时间压力', ja: '時間的プレッシャー' },
  overwhelmed_messy_context: { en: 'Messy context', es: 'Contexto desordenado', zh: '上下文混乱', ja: '状況が散らかっている' },
  distracted_tab_drift: { en: 'Tab drift', es: 'Deriva de pestañas', zh: '标签页漂移', ja: 'タブ逸脱' },
  distracted_notification: { en: 'Notification', es: 'Notificación', zh: '通知', ja: '通知' },
  distracted_thought_loop: { en: 'Thought loop', es: 'Bucle mental', zh: '思维循环', ja: '思考ループ' },
  distracted_person_interrupted: { en: 'Person interrupted', es: 'Interrupción de otra persona', zh: '被他人打断', ja: '人に中断された' },
  too_wired_restless: { en: 'Restless', es: 'Inquieto', zh: '坐立不安', ja: '落ち着かない' },
  too_wired_anxious: { en: 'Anxious', es: 'Ansioso', zh: '焦虑', ja: '不安' },
  too_wired_jittery: { en: 'Jittery', es: 'Acelerado', zh: '神经紧绷', ja: 'そわそわする' },
  too_wired_cannot_settle: { en: "Can't settle", es: 'No puedo calmarme', zh: '静不下来', ja: '落ち着けない' },
  too_tired_sleepy: { en: 'Sleepy', es: 'Con sueño', zh: '困倦', ja: '眠い' },
  too_tired_brain_fog: { en: 'Brain fog', es: 'Niebla mental', zh: '脑雾', ja: '頭がぼんやり' },
  too_tired_low_energy: { en: 'Low energy', es: 'Baja energía', zh: '精力低', ja: '低エネルギー' },
  too_tired_need_break: { en: 'Need a break', es: 'Necesito un descanso', zh: '需要休息', ja: '休憩が必要' },
  ruminating_self_criticism: { en: 'Self-criticism', es: 'Autocrítica', zh: '自我批评', ja: '自己批判' },
  ruminating_future_worry: { en: 'Future worry', es: 'Preocupación por el futuro', zh: '担忧未来', ja: '将来への不安' },
  ruminating_social_replay: { en: 'Social replay', es: 'Repetición social', zh: '社交回放', ja: '対人場面の反すう' },
  ruminating_decision_spiral: { en: 'Decision spiral', es: 'Espiral de decisión', zh: '决策打转', ja: '決断ループ' },
  irritable_overstimulated: { en: 'Overstimulated', es: 'Sobreestimulado', zh: '刺激过载', ja: '刺激過多' },
  irritable_frustrated: { en: 'Frustrated', es: 'Frustrado', zh: '挫败', ja: 'いら立ち' },
  irritable_interrupted: { en: 'Interrupted', es: 'Interrumpido', zh: '被打断', ja: '中断された' },
  irritable_need_pause: { en: 'Need pause', es: 'Necesito pausa', zh: '需要暂停', ja: 'いったん休止が必要' },
  too_big: { en: 'Too big', es: 'Demasiado grande', zh: '太大', ja: '大きすぎる' },
  unclear: { en: 'Unclear', es: 'Poco claro', zh: '不清楚', ja: '不明確' },
  boring: { en: 'Boring', es: 'Aburrido', zh: '无聊', ja: '退屈' },
  hard_start: { en: 'Hard start', es: 'Cuesta empezar', zh: '难开始', ja: '始めにくい' },
  hard_resume: { en: 'Hard resume', es: 'Cuesta retomar', zh: '难恢复', ja: '再開しにくい' }
};

export function themeDescription(language: AppLanguage, theme: AppTheme): string {
  return THEME_DESCRIPTIONS[theme]?.[language] ?? THEME_DESCRIPTIONS[theme]?.en ?? '';
}

export function fontDescription(language: AppLanguage, font: AppFont): string {
  return FONT_DESCRIPTIONS[font]?.[language] ?? FONT_DESCRIPTIONS[font]?.en ?? '';
}

export function visualStyleDescription(language: AppLanguage, style: AppVisualStyle): string {
  return VISUAL_STYLE_DESCRIPTIONS[style]?.[language] ?? VISUAL_STYLE_DESCRIPTIONS[style]?.en ?? '';
}

export function frictionSubtypeI18nLabel(language: AppLanguage, subtype: FrictionSubtype | null | undefined): string {
  if (!subtype) return t(language, 'common.na');
  return SUBTYPE_LABELS[subtype]?.[language] ?? SUBTYPE_LABELS[subtype]?.en ?? fallbackSubtypeLabel(subtype);
}

export function onboardingSteps(language: AppLanguage): Array<{ title: string; body: string; highlights: string[] }> {
  return [
    {
      title: t(language, 'onboarding.log_title'),
      body: t(language, 'onboarding.log_body'),
      highlights: [t(language, 'tab.log'), t(language, 'log.start_stuck')]
    },
    {
      title: t(language, 'onboarding.rescue_title'),
      body: t(language, 'onboarding.rescue_body'),
      highlights: [t(language, 'tab.rescue'), rescueKindLabel(language, 'reset_2m'), rescueKindLabel(language, 'focus_25m')]
    },
    {
      title: t(language, 'onboarding.park_title'),
      body: t(language, 'onboarding.park_body'),
      highlights: [t(language, 'topbar.park')]
    },
    {
      title: t(language, 'onboarding.history_title'),
      body: t(language, 'onboarding.history_body'),
      highlights: [t(language, 'tab.history')]
    },
    {
      title: t(language, 'onboarding.insights_title'),
      body: t(language, 'onboarding.insights_body'),
      highlights: [t(language, 'tab.insights')]
    },
    {
      title: t(language, 'onboarding.options_title'),
      body: t(language, 'onboarding.options_body'),
      highlights: [t(language, 'tab.options')]
    },
    {
      title: t(language, 'onboarding.safety_title'),
      body: t(language, 'onboarding.safety_body'),
      highlights: [t(language, 'options.privacy_data'), t(language, 'options.contact_support')]
    }
  ];
}
